/**
 * TrainEditPage — full editor for a process train.
 *
 * Routes:
 *   /train-edit              → new blank train
 *   /train-edit?id=NN        → edit existing DB train (loaded via apiListTrains)
 *
 * Capabilities:
 *  • Sample picker popup — preload influent from any sample assigned to the
 *    customer (or anywhere if no customer context).
 *  • Flow + full water-quality sliders (overrides loaded sample).
 *  • Equipment library popup — add stages from: Aeration Column, Greensand /
 *    Birm / Pyrolusite catalytic, Ultrafiltration, Multimedia, Cartridge polish,
 *    PACl coagulant dosing. Re-ordering and removal supported.
 *  • Per-stage compact config panel with the key knobs for each equipment type.
 *  • Live cascade simulation — each stage's outlet becomes the next stage's
 *    inlet. Final outlet compared against RO design basis (Fe ≤ 0.01 mg/L,
 *    Mn = 0, SDI ≤ 3, TSS ≤ 5 mg/L) and a green/red banner displayed.
 *  • SVG flow diagram (all text ≥ 12 px).
 *  • Save to DB via /api/process-trains/save.php (insert or update).
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Layers, Wind, CircleDot, Filter, FlaskConical, Droplets, Save, Plus, X, ArrowRight,
  Beaker, Gauge, CheckCircle2, AlertCircle, ShieldAlert, ArrowDown, Database,
  Workflow, Sparkles, Trash2, MoveUp, MoveDown, Eye, EyeOff,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { apiListInfluentSamples, apiListCustomers } from '@/lib/phpApi';
import { getLocalProcessTrains, saveLocalProcessTrain, updateLocalProcessTrain, getLocalInfluentSamples } from '@/lib/hybridStorage';
import { calcAerationColumn } from '@/pages/AerationColumnSpecPage';
import { calcGreensand } from '@/pages/GreensandSpecPage';
import { calcUF } from '@/pages/UFSpecPage';
import { calcCascade } from '@/pages/CascadeAeratorSpecPage';
import { calcUV } from '@/pages/UVDisinfectionSpecPage';
import { simulateUnit as simulateWWStage, LABEL_MAP as WW_LABEL_MAP, rawParamsToWQ, type WaterQuality } from '@/lib/processTrainSimulator';
import { Zap, Recycle, Waves, Trash, Container, Microscope } from 'lucide-react';
import { generateTrainPdfReport, type TrainReportInput } from '@/lib/trainReportPdf';

/* ─── Domain types ──────────────────────────────────────── */

interface Water {
  flow: number;        // m³/h
  fe: number;          // mg/L
  mn: number;          // mg/L
  ph: number;
  alkalinity: number;  // mg/L CaCO3
  tss: number;         // mg/L
  turbidity: number;   // NTU
  sdi: number;
  doIn: number;        // mg/L
  temp: number;        // °C
  hardness: number;    // mg/L CaCO3
  flowDay: number;     // m³/day (derived)
  nh3: number;         // mg/L NH3-N (≙ ammoniaN; ammonia stripping + WW)
  chlorineResidual?: number; // mg/L free Cl (informational; from electrochlorination)
  // ── Wastewater quality (carried so WW process units can cascade losslessly) ──
  bod5?: number;            // mg/L O₂
  cod?: number;             // mg/L O₂
  oilGrease?: number;       // mg/L
  totalNitrogen?: number;   // mg/L N
  totalPhosphorus?: number; // mg/L P
  tds?: number;             // mg/L
  conductivity?: number;    // µS/cm
  color?: number;           // Pt-Co
  sulfides?: number;        // mg/L
  surfactants?: number;     // mg/L
  phenol?: number;          // mg/L
  lead?: number; mercury?: number; cadmium?: number; chromium?: number;
  nickel?: number; zinc?: number; copper?: number; // mg/L metals
}

const RO_TARGETS = { fe: 0.01, mn: 0, sdi: 3, tss: 5 };

/* ─── RO Membrane catalogue ───────────────────────────────
   Public-domain element specifications digested from each manufacturer's
   product data sheets (Hydranautics, DuPont/FilmTec, Toray, LG Chem, Suez/Veolia).
   All feed-water limits are upper hard limits; values below them are safe.
   Element prices are typical UK list (2024–2026), ±20% depending on volume.
   Nominal life is "design service life under best-practice operation"
   per manufacturer guidance — actual life depends heavily on feed quality
   (Fe, Mn, SDI, free Cl), cleaning frequency, recovery and temperature. */
export interface ROMembrane {
  id: string;
  brand: string;
  model: string;
  classLabel: string;     // 'Brackish low-energy', 'Brackish high-rejection', 'Seawater', etc.
  saltRejectionPct: number;
  feMaxMgL: number;
  mnMaxMgL: number;
  sdiMax: number;
  freeChlorineMaxMgL: number; // hard limit, hours-scale damage above
  tempMaxC: number;
  pHmin: number;
  pHmax: number;
  nominalLifeYears: number;
  elementPriceGBP: number;
}

export const RO_MEMBRANES: ROMembrane[] = [
  { id: 'hyd_espa2_ld',    brand: 'Hydranautics', model: 'ESPA2-LD',        classLabel: 'Brackish low-energy',     saltRejectionPct: 99.6, feMaxMgL: 0.05, mnMaxMgL: 0.05, sdiMax: 5, freeChlorineMaxMgL: 0.1, tempMaxC: 45, pHmin: 2, pHmax: 11, nominalLifeYears: 7, elementPriceGBP: 450 },
  { id: 'hyd_cpa5_ld',     brand: 'Hydranautics', model: 'CPA5-LD',         classLabel: 'Brackish high-rejection', saltRejectionPct: 99.7, feMaxMgL: 0.05, mnMaxMgL: 0.05, sdiMax: 5, freeChlorineMaxMgL: 0.1, tempMaxC: 45, pHmin: 2, pHmax: 11, nominalLifeYears: 7, elementPriceGBP: 550 },
  { id: 'dupont_bw30_400', brand: 'DuPont',       model: 'FILMTEC BW30-400', classLabel: 'Brackish high-rejection', saltRejectionPct: 99.5, feMaxMgL: 0.05, mnMaxMgL: 0.05, sdiMax: 5, freeChlorineMaxMgL: 0.1, tempMaxC: 45, pHmin: 2, pHmax: 11, nominalLifeYears: 6, elementPriceGBP: 500 },
  { id: 'dupont_xle_440',  brand: 'DuPont',       model: 'FILMTEC XLE-440',  classLabel: 'Brackish low-energy',     saltRejectionPct: 99.0, feMaxMgL: 0.05, mnMaxMgL: 0.05, sdiMax: 5, freeChlorineMaxMgL: 0.1, tempMaxC: 45, pHmin: 2, pHmax: 11, nominalLifeYears: 6, elementPriceGBP: 420 },
  { id: 'toray_tm720_440', brand: 'Toray',        model: 'TM720-440',        classLabel: 'Brackish standard',       saltRejectionPct: 99.6, feMaxMgL: 0.05, mnMaxMgL: 0.05, sdiMax: 5, freeChlorineMaxMgL: 0.1, tempMaxC: 45, pHmin: 3, pHmax: 10, nominalLifeYears: 6, elementPriceGBP: 480 },
  { id: 'lg_bw_400es',     brand: 'LG Chem',      model: 'BW 400 ES',        classLabel: 'Brackish low-energy',     saltRejectionPct: 99.5, feMaxMgL: 0.05, mnMaxMgL: 0.05, sdiMax: 5, freeChlorineMaxMgL: 0.1, tempMaxC: 45, pHmin: 2, pHmax: 11, nominalLifeYears: 7, elementPriceGBP: 460 },
  { id: 'suez_ag_440lf',   brand: 'Suez (Veolia)', model: 'AG-440LF',        classLabel: 'Brackish low-fouling',    saltRejectionPct: 99.5, feMaxMgL: 0.05, mnMaxMgL: 0.05, sdiMax: 5, freeChlorineMaxMgL: 0.1, tempMaxC: 45, pHmin: 4, pHmax: 11, nominalLifeYears: 6, elementPriceGBP: 520 },
  { id: 'dupont_sw30hr',   brand: 'DuPont',       model: 'FILMTEC SW30HR-380', classLabel: 'Seawater high-rejection', saltRejectionPct: 99.8, feMaxMgL: 0.05, mnMaxMgL: 0.05, sdiMax: 4, freeChlorineMaxMgL: 0.1, tempMaxC: 45, pHmin: 2, pHmax: 11, nominalLifeYears: 5, elementPriceGBP: 800 },
];

export interface ROLifeEstimate {
  membrane: ROMembrane;
  baseYears: number;
  adjustedYears: number;
  fePenaltyYears: number;
  mnPenaltyYears: number;
  sdiPenaltyYears: number;
  tempPenaltyYears: number;
  chlorineKill: boolean;
  warnings: string[];
  elementsAssumed: number;        // typical 1–7 elements per pressure vessel
  replacementCostGBPperCycle: number;
  cost10yrGBP: number;
}

export function estimateROLife(feed: { fe: number; mn: number; sdi: number; tss: number; turbidity: number; temp: number; freeChlorine?: number }, membrane: ROMembrane, elementsAssumed = 6): ROLifeEstimate {
  const warnings: string[] = [];
  let years = membrane.nominalLifeYears;

  // Fe penalty — exponential damage above limit
  let fePenalty = 0;
  if (feed.fe > membrane.feMaxMgL) {
    const ratio = feed.fe / membrane.feMaxMgL;
    fePenalty = years * (1 - Math.pow(1 / ratio, 1.5));
    warnings.push(`Fe ${feed.fe.toFixed(3)} mg/L exceeds ${membrane.feMaxMgL} mg/L limit (${ratio.toFixed(1)}×) — irreversible iron fouling.`);
  }
  // Mn penalty — same model
  let mnPenalty = 0;
  if (feed.mn > membrane.mnMaxMgL) {
    const ratio = feed.mn / membrane.mnMaxMgL;
    mnPenalty = years * (1 - Math.pow(1 / ratio, 1.5)) * 0.7; // Mn slightly less damaging than Fe
    warnings.push(`Mn ${feed.mn.toFixed(3)} mg/L exceeds ${membrane.mnMaxMgL} mg/L limit.`);
  }
  // SDI penalty — linear above 3 (typical RO design SDI ≤ 3)
  let sdiPenalty = 0;
  const sdiSoftLimit = 3;
  if (feed.sdi > sdiSoftLimit) {
    sdiPenalty = Math.min(years * 0.45, (feed.sdi - sdiSoftLimit) * 0.6);
    warnings.push(`SDI ${feed.sdi.toFixed(1)} > ${sdiSoftLimit} → colloidal fouling, increased CIP frequency.`);
  }
  // Temperature — life shortens above 20°C (Arrhenius-style)
  let tempPenalty = 0;
  if (feed.temp > 25) {
    tempPenalty = (feed.temp - 25) * 0.08;
    warnings.push(`Feed ${feed.temp.toFixed(0)} °C above 25 °C — hydrolysis & biofouling rate higher.`);
  }
  if (feed.temp > membrane.tempMaxC) {
    warnings.push(`⚠ Feed temperature ${feed.temp.toFixed(0)} °C exceeds membrane max ${membrane.tempMaxC} °C.`);
  }
  // Free chlorine — immediate kill above 0.1 mg/L for polyamide
  let chlorineKill = false;
  if ((feed.freeChlorine ?? 0) > membrane.freeChlorineMaxMgL) {
    chlorineKill = true;
    warnings.push(`⚠ FREE CHLORINE ${(feed.freeChlorine ?? 0).toFixed(2)} mg/L exceeds ${membrane.freeChlorineMaxMgL} mg/L — polyamide membrane will fail within weeks. Install SMBS / carbon dechlorination upstream.`);
  }

  const adjustedYears = chlorineKill ? 0.5 : Math.max(0.5, years - fePenalty - mnPenalty - sdiPenalty - tempPenalty);
  const replacementCostGBPperCycle = elementsAssumed * membrane.elementPriceGBP;
  const cyclesIn10Years = 10 / adjustedYears;
  const cost10yrGBP = replacementCostGBPperCycle * cyclesIn10Years;

  return {
    membrane,
    baseYears: years,
    adjustedYears,
    fePenaltyYears: fePenalty,
    mnPenaltyYears: mnPenalty,
    sdiPenaltyYears: sdiPenalty,
    tempPenaltyYears: tempPenalty,
    chlorineKill,
    warnings,
    elementsAssumed,
    replacementCostGBPperCycle,
    cost10yrGBP,
  };
}

type EquipmentKey =
  | 'aeration_column' | 'cascade_aerator' | 'greensand' | 'uf'
  | 'multimedia' | 'dual_media_filter' | 'sand_filter_single' | 'gac_filter'
  | 'cartridge_polish' | 'pacl_dosing' | 'uv_disinfection'
  | 'desalination_daf' | 'ammonia_stripper' | 'electrochlorination' | 'orifice'
  // ── Wastewater units (keys match the wastewater process-train simulator) ──
  | 'screening' | 'eq' | 'ph' | 'buffer' | 'daf' | 'lamella'
  | 'aeration' | 'secondary' | 'mbbr' | 'uasb' | 'mbr' | 'gac' | 'sludge';

/** Keys that are wastewater process units (drive the WW cascade + the
 *  "discharge compliance on Process Train page" banner). */
const WW_KEYS: ReadonlySet<string> = new Set([
  'screening', 'eq', 'ph', 'buffer', 'daf', 'lamella',
  'aeration', 'secondary', 'mbbr', 'uasb', 'mbr', 'gac', 'sludge',
]);

interface StageConfig {
  id: string;
  key: EquipmentKey;
  params: Record<string, any>;
  enabled: boolean;
}

interface StageResult {
  in: Water; out: Water;
  notes: string;
  capexGBP: number;
  opexGBPperYear: number;
}

/* ─── Equipment library ─────────────────────────────────── */

interface SliderDef { key: string; label: string; min: number; max: number; step: number; unit: string; info?: string; }
interface SelectDef { key: string; label: string; options: { value: string; label: string }[]; }

interface SimContext { upstreamKeys: EquipmentKey[] }

interface EquipmentDef {
  key: EquipmentKey;
  name: string;
  icon: any;
  color: string;
  description: string;
  defaultParams: Record<string, any>;
  sliders: SliderDef[];                       // inline strip on stage card
  selectFields?: SelectDef[];
  advancedSliders?: SliderDef[];              // shown ONLY in detail popup
  advancedSelects?: SelectDef[];
  scienceNotes?: string[];                    // shown in detail popup
  simulate: (params: Record<string, any>, input: Water, ctx?: SimContext) => { out: Water; notes: string; capexGBP: number; opexGBPperYear: number };
}

/* ─── Wastewater bridge ──────────────────────────────────────
   The wastewater process units live in processTrainSimulator.ts and operate
   on its WaterQuality model. These helpers convert the editor's Water object
   to/from WaterQuality so the SAME validated removal physics drives the live
   cascade here. WW concentrations ride on the Water object (optional fields)
   so each stage's outlet faithfully becomes the next stage's inlet. */
function waterToWQ(w: Water): WaterQuality {
  return {
    bod5: w.bod5 ?? 0, cod: w.cod ?? 0, tss: w.tss ?? 0, oilGrease: w.oilGrease ?? 0,
    ammoniaN: w.nh3 ?? 0, totalNitrogen: w.totalNitrogen ?? 0, totalPhosphorus: w.totalPhosphorus ?? 0,
    tds: w.tds ?? 0, ph: w.ph ?? 7, temperature: w.temp ?? 20, conductivity: w.conductivity ?? 0,
    lead: w.lead ?? 0, mercury: w.mercury ?? 0, cadmium: w.cadmium ?? 0, chromium: w.chromium ?? 0,
    nickel: w.nickel ?? 0, zinc: w.zinc ?? 0, copper: w.copper ?? 0, phenol: w.phenol ?? 0,
    sulfides: w.sulfides ?? 0, surfactants: w.surfactants ?? 0, color: w.color ?? 0, turbidity: w.turbidity ?? 0,
  };
}
function applyWQ(base: Water, eff: WaterQuality): Water {
  return {
    ...base,
    tss: eff.tss, turbidity: eff.turbidity, ph: eff.ph, nh3: eff.ammoniaN,
    bod5: eff.bod5, cod: eff.cod, oilGrease: eff.oilGrease, totalNitrogen: eff.totalNitrogen,
    totalPhosphorus: eff.totalPhosphorus, tds: eff.tds, conductivity: eff.conductivity, color: eff.color,
    sulfides: eff.sulfides, surfactants: eff.surfactants, phenol: eff.phenol,
    lead: eff.lead, mercury: eff.mercury, cadmium: eff.cadmium, chromium: eff.chromium,
    nickel: eff.nickel, zinc: eff.zinc, copper: eff.copper,
  };
}
/** Run a wastewater unit via the shared simulator; estimate capex/opex by flow. */
function wwStage(key: string, input: Water, capexGBP: number, opexGBPperYear: number) {
  const r = simulateWWStage(key, waterToWQ(input));
  return { out: applyWQ(input, r.effluent), notes: r.notes[0] ?? WW_LABEL_MAP[key] ?? key, capexGBP, opexGBPperYear };
}
/** Build a standard wastewater EquipmentDef from the shared simulator. */
function wwDef(key: EquipmentKey, name: string, icon: any, color: string, description: string,
               capexBase: number, capexPerM3d: number, opexPerM3d: number, scienceNotes: string[]): EquipmentDef {
  return {
    key, name, icon, color, description,
    defaultParams: {}, sliders: [], scienceNotes,
    simulate: (_p, input) => {
      const Qd = Math.max(1, input.flowDay || input.flow * 24);
      return wwStage(key, input, capexBase + capexPerM3d * Qd, opexPerM3d * Qd);
    },
  };
}

const EQUIPMENT_LIBRARY: Record<EquipmentKey, EquipmentDef> = {
  aeration_column: {
    key: 'aeration_column', name: 'Aeration Column', icon: Wind, color: '#06b6d4',
    description: 'Pre-oxidises dissolved Fe²⁺ → Fe³⁺ floc; strips H₂S; raises DO. Recommended ahead of catalytic media.',
    defaultParams: { columnDiameter: 0.9, columnHeight: 2.5, airToWaterRatio: 8, airPressure: 0.4, packingSize: 50, packingType: 'tellerette', freeboard: 0.5 },
    sliders: [
      { key: 'columnDiameter', label: 'Diameter', min: 0.3, max: 3, step: 0.05, unit: 'm' },
      { key: 'columnHeight',   label: 'Packed height', min: 0.5, max: 6, step: 0.1, unit: 'm' },
      { key: 'airToWaterRatio',label: 'Air : Water', min: 1, max: 30, step: 0.5, unit: 'Nm³/m³' },
      { key: 'airPressure',    label: 'Blower ΔP', min: 0.1, max: 1.2, step: 0.05, unit: 'bar' },
      { key: 'packingSize',    label: 'Packing size', min: 15, max: 100, step: 5, unit: 'mm' },
    ],
    advancedSliders: [
      { key: 'freeboard', label: 'Freeboard above bed', min: 0.2, max: 1.5, step: 0.1, unit: 'm', info: 'Gas-disengagement space above the packed bed.' },
    ],
    advancedSelects: [
      { key: 'packingType', label: 'Packing type', options: [
        { value: 'tellerette', label: 'Tellerette (high voidage)' },
        { value: 'pall_ring', label: 'Pall ring (balanced)' },
        { value: 'random_polypropylene', label: 'Random PP saddles (low cost)' },
        { value: 'tray_cascade', label: 'Cascade tray (simple)' },
      ]},
    ],
    scienceNotes: [
      'Stumm & Lee (1961) Fe oxidation: d[Fe²⁺]/dt = -k·[Fe²⁺]·pO₂·[OH⁻]² → rate doubles per pH unit (squared OH⁻ dependence).',
      'Mn²⁺ needs pH ≥ 9 for meaningful autoxidation; below that, expect a catalytic stage downstream.',
      'H₂S Henry coefficient ≈ 0.4 mol/(L·atm) → easy strip with G/L ≥ 5.',
      'Dissolved O₂ saturation ≈ 14.6 − 0.394·T + 0.0076·T² (mg/L). Birm downstream needs DO ≥ 15% of Fe load.',
    ],
    simulate: (p, input) => {
      const r = calcAerationColumn({
        flowRate: input.flow, influentFe: input.fe, influentMn: input.mn, influentH2S: 0,
        ph: input.ph, alkalinity: input.alkalinity, temperature: input.temp,
        columnDiameter: p.columnDiameter, columnHeight: p.columnHeight, freeboard: 0.5,
        packingType: 'tellerette', packingSize: p.packingSize,
        airToWaterRatio: p.airToWaterRatio, airPressure: p.airPressure,
      });
      return {
        out: { ...input, fe: r.outletFe, mn: r.outletMn, doIn: r.doAchieved, turbidity: input.turbidity + 0.1 },
        notes: `Fe ox ${r.feOxidationPct.toFixed(0)}% · DO ${r.doAchieved.toFixed(1)} mg/L · EBCT ${r.ebct.toFixed(0)}s · ${r.blowerPower.toFixed(2)} kW blower`,
        capexGBP: 18000 + p.columnDiameter * p.columnHeight * 2500,
        opexGBPperYear: r.blowerPower * 8760 * 0.18,
      };
    },
  },

  cascade_aerator: {
    key: 'cascade_aerator', name: 'Cascade Aerator (gravity tower)', icon: Workflow, color: '#0ea5e9',
    description: 'Open gravity cascade — water falls in steps over weirs, picking up O₂ from ambient air. No blower; passive ΔP. Drop-in alternative to the aeration column.',
    defaultParams: {
      flowRate: 24, inletDO: 2, temperature: 12,
      stepHeightAvg: 0.5, stepCount: 5,
      weirLength: 2.0, weirHeight: 0.3,
      towerDiameter: 1.4, towerHeight: 3.0,
      waterQualityA: 1.0, weirTypeB: 1.0,
      exhaustO2Pct: 19, targetDO: 6,
    },
    sliders: [
      { key: 'stepCount',     label: 'Steps',           min: 2, max: 10, step: 1, unit: '' },
      { key: 'stepHeightAvg', label: 'Step drop',       min: 0.2, max: 1.5, step: 0.05, unit: 'm' },
      { key: 'weirLength',    label: 'Weir length',     min: 0.5, max: 8, step: 0.1, unit: 'm' },
      { key: 'towerDiameter', label: 'Tower Ø',         min: 0.6, max: 4, step: 0.1, unit: 'm' },
      { key: 'towerHeight',   label: 'Total height',    min: 1.0, max: 8, step: 0.1, unit: 'm' },
    ],
    advancedSliders: [
      { key: 'weirHeight', label: 'Weir crest above floor', min: 0.1, max: 1.0, step: 0.05, unit: 'm' },
      { key: 'inletDO',    label: 'Inlet DO',               min: 0,   max: 10,  step: 0.1,  unit: 'mg/L', info: 'DO entering the cascade (borehole ≈ 0–2).' },
      { key: 'exhaustO2Pct', label: 'Exhaust O₂',           min: 15,  max: 21,  step: 0.1,  unit: '%',    info: 'Used to size required air-change rate.' },
      { key: 'targetDO',   label: 'Target outlet DO',       min: 4,   max: 10,  step: 0.1,  unit: 'mg/L' },
    ],
    advancedSelects: [
      { key: 'waterQualityA', label: 'Water-quality (a)', options: [
        { value: '0.85', label: 'Clean (a = 0.85)' },
        { value: '1.0',  label: 'Typical (a = 1.0)' },
        { value: '1.25', label: 'Hard / saline (a = 1.25)' },
      ]},
      { key: 'weirTypeB', label: 'Weir type (b)', options: [
        { value: '1.0', label: 'Sharp-crested (b = 1.0)' },
        { value: '1.3', label: 'Stepped / aerated (b = 1.3)' },
      ]},
    ],
    scienceNotes: [
      'Deficit-ratio method (Gameson, Twort-Ratnayaka-Brandt): r = 1 + 0.5·a·b·h·(1 + 0.046·T) per step.',
      'Each step oxygen gain: ΔDO = (Cs − Cu) · (1 − 1/r).',
      'No blower required — works on the available 9 bar borehole pressure; only ΔP is the tower height + outlet free fall.',
      'Strips H₂S well thanks to constant fresh-air contact; less compact than a pressure column but lower opex.',
    ],
    simulate: (p, input) => {
      // Build N identical steps from stepCount + stepHeightAvg.
      const steps = Array.from({ length: Math.max(1, Math.round(p.stepCount)) }, () => ({ h: p.stepHeightAvg }));
      const r = calcCascade({
        flowRate: input.flow,
        inletDO: p.inletDO ?? input.doIn ?? 0,
        temperature: input.temp,
        waterQualityA: Number(p.waterQualityA),
        weirTypeB: Number(p.weirTypeB),
        steps,
        weirLength: p.weirLength,
        weirHeight: p.weirHeight,
        towerDiameter: p.towerDiameter,
        towerHeight: p.towerHeight,
        exhaustO2Pct: p.exhaustO2Pct,
        targetDO: p.targetDO,
      });
      // Simple Fe oxidation model — Stumm-Lee, paralleling the column.
      // Effective EBCT in the cascade ≈ sum of step fall times.
      const ebct = steps.reduce((sum, s) => sum + Math.sqrt(2 * s.h / 9.81), 0); // seconds
      const phFactor = Math.max(0, Math.pow(10, (input.ph - 7) * 2));
      const kFe = 0.05 * phFactor;
      const feOx = Math.min(99, (1 - Math.exp(-kFe * ebct)) * 100);
      const outFe = input.fe * (1 - feOx / 100);
      const kMn  = 0.005 * Math.pow(10, Math.max(0, input.ph - 8) * 2);
      const mnOx = Math.min(40, (1 - Math.exp(-kMn * ebct)) * 100);
      const outMn = input.mn * (1 - mnOx / 100);
      const stepsActual = steps.length;
      return {
        out: { ...input, fe: outFe, mn: outMn, doIn: r.finalDO, turbidity: input.turbidity + 0.05 },
        notes: `${stepsActual} steps · DO ${r.finalDO.toFixed(1)} mg/L (${r.finalPctSat.toFixed(0)}% sat) · Fe ox ${feOx.toFixed(0)}% · weir load ${r.weirLoadingRate.toFixed(1)} L/s/m`,
        capexGBP: 14000 + p.towerDiameter * p.towerHeight * 1800 + stepsActual * 1100,
        opexGBPperYear: 600, // no blower; passive — only inspection & cleaning
      };
    },
  },

  greensand: {
    key: 'greensand', name: 'Catalytic Filter (Greensand / Birm)', icon: Filter, color: '#10b981',
    description: 'Catalytic Fe/Mn removal media. Greensand Plus needs KMnO₄ regen; Birm uses DO from upstream aeration only.',
    defaultParams: { media: 'greensand_plus', vesselDiameter: 1.6, bedDepth: 0.9, numVessels: 2, kmno4Dose: 1.5, backwashRate: 30, arrangement: 'duplex_parallel', freeboard: 0.5, serviceVelocity: 8 },
    advancedSliders: [
      { key: 'freeboard', label: 'Freeboard', min: 0.3, max: 1.5, step: 0.05, unit: 'm' },
      { key: 'serviceVelocity', label: 'Target service velocity', min: 4, max: 15, step: 0.5, unit: 'm/h', info: 'Used for sizing checks; actual depends on flow & vessel area.' },
    ],
    scienceNotes: [
      'Greensand Plus envelope: pH 6.2–8.8, v 5–12 m/h, Fe up to 15 mg/L, Mn up to 10 mg/L. KMnO₄ stoichiometric ≈ 1·[Fe] + 2·[Mn] mg/L (×1.3 safety).',
      'Birm envelope: pH 6.8–9.0, needs DO ≥ 15% of total Fe by mass; ineffective on Mn above ~1 mg/L.',
      'Pyrolusite: heavy natural MnO₂; high capacity but needs robust underdrain & higher backwash rate.',
      'Avoid KMnO₄ carryover to downstream RO membranes (pink-water risk) — set continuous-regen dose conservatively.',
    ],
    sliders: [
      { key: 'vesselDiameter', label: 'Vessel Ø', min: 0.5, max: 3, step: 0.05, unit: 'm' },
      { key: 'bedDepth', label: 'Bed depth', min: 0.5, max: 1.5, step: 0.05, unit: 'm' },
      { key: 'numVessels', label: 'Vessels', min: 1, max: 4, step: 1, unit: '' },
      { key: 'kmno4Dose', label: 'KMnO₄ dose', min: 0, max: 10, step: 0.1, unit: 'mg/L' },
      { key: 'backwashRate', label: 'Backwash rate', min: 15, max: 60, step: 1, unit: 'm³/m²·h' },
    ],
    selectFields: [
      { key: 'media', label: 'Media', options: [
        { value: 'greensand_plus', label: 'Greensand Plus' },
        { value: 'birm', label: 'Birm' },
        { value: 'pyrolusite', label: 'Pyrolusite' },
      ]},
      { key: 'arrangement', label: 'Arrangement', options: [
        { value: 'duplex_parallel', label: 'Duplex parallel' },
        { value: 'duplex_duty_standby', label: 'Duplex duty/standby' },
        { value: 'triplex', label: 'Triplex 2+1' },
      ]},
    ],
    simulate: (p, input) => {
      const r = calcGreensand({
        flowRate: input.flow, influentFe: input.fe, influentMn: input.mn,
        ph: input.ph, doIn: input.doIn, temperature: input.temp,
        vesselDiameter: p.vesselDiameter, bedDepth: p.bedDepth, freeboard: 0.5,
        numVessels: p.numVessels, arrangement: p.arrangement,
        media: p.media, kmno4Dose: p.kmno4Dose,
        serviceVelocity: 8, backwashRate: p.backwashRate,
      });
      return {
        out: { ...input, fe: r.outletFe, mn: r.outletMn, sdi: Math.min(input.sdi, 3.2), turbidity: Math.max(0.08, input.turbidity * 0.4) },
        notes: `Fe ${r.feRemovalPct.toFixed(0)}% · Mn ${r.mnRemovalPct.toFixed(0)}% · v ${r.actualVelocity.toFixed(1)} m/h · KMnO₄ ${r.kmno4ConsumptionKgD.toFixed(2)} kg/d`,
        capexGBP: 22000 * p.numVessels + p.vesselDiameter * 4000 * p.numVessels,
        opexGBPperYear: r.kmno4ConsumptionKgD * 365 * 4 + 1200, // rough KMnO4 £4/kg + maintenance
      };
    },
  },

  uf: {
    key: 'uf', name: 'Ultrafiltration (UF)', icon: CircleDot, color: '#a855f7',
    description: 'Hollow-fibre PVDF membrane barrier. Sub-µm rejection. Guarantees SDI <3.',
    defaultParams: { fibre: 'pvdf', poreSize: 0.03, numModules: 4, moduleArea: 50, flux: 60, recovery: 92, fibreOD: 1.3, fibreID: 0.7, fibresPerModule: 9000, backwashInterval: 30, backwashDuration: 60, cebInterval: 12, cipInterval: 30 },
    advancedSliders: [
      { key: 'fibreOD', label: 'Fibre OD', min: 0.5, max: 2.5, step: 0.05, unit: 'mm' },
      { key: 'fibreID', label: 'Fibre ID', min: 0.3, max: 1.5, step: 0.05, unit: 'mm' },
      { key: 'fibresPerModule', label: 'Fibres / module', min: 2000, max: 20000, step: 500, unit: '' },
      { key: 'backwashInterval', label: 'BW interval', min: 15, max: 120, step: 5, unit: 'min' },
      { key: 'backwashDuration', label: 'BW duration', min: 20, max: 120, step: 5, unit: 's' },
      { key: 'cebInterval', label: 'CEB interval', min: 2, max: 48, step: 1, unit: 'h' },
      { key: 'cipInterval', label: 'CIP interval', min: 7, max: 90, step: 1, unit: 'd' },
    ],
    scienceNotes: [
      'Pore 0.02–0.04 µm physically rejects particles >0.1 µm by size exclusion. SDI guarantee <3 because the sub-µm material that drives SDI is screened out.',
      'UF does NOT remove dissolved species — Fe²⁺, Mn²⁺, salts pass through. Always place a catalytic stage (Greensand/Birm) AND aeration upstream so iron is in oxidised floc form before reaching the membrane.',
      'Temperature-corrected flux ≈ measured × exp(0.0239·(20−T°C)). Lower temperature → higher TMP at same flux. Design 20% headroom for winter ops.',
      'PVDF: chlorine tolerance up to ~200 ppm·h, CEB/CIP-friendly. PES & PAN more sensitive to Cl₂.',
      'Iron carryover from upstream catalytic stage must be <0.1 mg/L — Fe³⁺ floc fouls fibre lumens; CIP recovery is incomplete after repeated exposure.',
      'Typical operating envelope: flux 30–80 LMH, recovery 90–95%, TMP <1 bar clean. CEB every 8–24 h (NaOCl 50–100 mg/L), CIP every 30–60 d (acid + caustic).',
    ],
    sliders: [
      { key: 'poreSize', label: 'Pore size', min: 0.01, max: 0.1, step: 0.005, unit: 'µm' },
      { key: 'numModules', label: 'Modules', min: 1, max: 16, step: 1, unit: '' },
      { key: 'moduleArea', label: 'm²/module', min: 20, max: 80, step: 5, unit: 'm²' },
      { key: 'flux', label: 'Flux', min: 20, max: 120, step: 1, unit: 'LMH' },
      { key: 'recovery', label: 'Recovery', min: 75, max: 99, step: 0.5, unit: '%' },
    ],
    selectFields: [
      { key: 'fibre', label: 'Membrane', options: [
        { value: 'pvdf', label: 'PVDF' },
        { value: 'pes', label: 'PES' },
        { value: 'pan', label: 'PAN' },
      ]},
    ],
    simulate: (p, input) => {
      const r = calcUF({
        flowRate: input.flow, influentTurbidity: input.turbidity, influentTSS: input.tss, influentSDI: input.sdi,
        influentFe: input.fe, temperature: input.temp,
        configuration: 'outside_in_pressurised', fibre: p.fibre, poreSize: p.poreSize,
        fibreOD: 1.3, fibreID: 0.7, fibresPerModule: 9000, moduleArea: p.moduleArea,
        numModules: p.numModules, flux: p.flux, recovery: p.recovery,
        backwashInterval: 30, backwashDuration: 60, cebInterval: 12, cipInterval: 30,
      });
      // Chemistry: UF is a size-exclusion barrier (~0.02–0.04 µm). It physically
      // rejects every particle larger than ~3× pore size, so it captures Fe³⁺ floc,
      // Mn³⁺/Mn⁴⁺ floc, colloids, bacteria, viruses. It does NOT remove dissolved
      // species (Fe²⁺, Mn²⁺, salts) — those pass through ionically. Approximate the
      // oxidised fraction from upstream dissolved oxygen: if DO > 2 mg/L the upstream
      // aeration / catalytic media have done their job and ~95% of Fe is now floc;
      // without DO most Fe is still dissolved Fe²⁺ and slips through.
      const oxidisedFrac = input.doIn > 2 ? 0.95 : 0.20;
      const feFlocIn = input.fe * oxidisedFrac;
      const feDissolvedIn = input.fe * (1 - oxidisedFrac);
      const feOut = feFlocIn * 0.02 + feDissolvedIn;   // UF removes 98% of floc, 0% of dissolved
      const mnFlocIn = input.mn * oxidisedFrac;
      const mnDissolvedIn = input.mn * (1 - oxidisedFrac);
      const mnOut = mnFlocIn * 0.05 + mnDissolvedIn;
      // Particulate KPIs: UF effectively zeroes TSS / turbidity / SDI within physics.
      const tssOut = Math.max(0.1, input.tss * 0.02);
      const turbOut = Math.max(0.02, Math.min(0.2, input.turbidity * 0.005));
      const sdiOut = r.outletSDI;
      return {
        out: { ...input, fe: feOut, mn: mnOut, sdi: sdiOut, turbidity: turbOut, tss: tssOut },
        notes: `Rejection ${r.particleRejectionPct.toFixed(2)}% · flux ${r.actualFlux.toFixed(0)} LMH · TMP ${r.tmp.toFixed(2)} bar · SDI out ${sdiOut.toFixed(1)} · DO-derived Fe-floc fraction ${(oxidisedFrac*100).toFixed(0)}%`,
        capexGBP: 60000 + p.numModules * 8500,
        opexGBPperYear: r.cebChemMonthlyKg * 12 * 2 + r.cipChemMonthlyKg * 12 * 5 + 2400,
      };
    },
  },

  multimedia: {
    key: 'multimedia', name: 'Multimedia Filter', icon: Layers, color: '#fbbf24',
    description: 'Anthracite + sand + garnet bed. Practical cut-off ~10–20 µm without coagulation; with upstream PACl can capture flocs efficiently. Does NOT remove dissolved Fe²⁺/Mn²⁺.',
    defaultParams: { vesselDiameter: 1.4, bedDepth: 1.0, numVessels: 2, anthraciteDepth: 0.5, sandDepth: 0.3, garnetDepth: 0.2, anthraciteES: 1.4, sandES: 0.55, garnetES: 0.3, backwashRate: 25 },
    sliders: [
      { key: 'vesselDiameter', label: 'Vessel Ø', min: 0.5, max: 3, step: 0.05, unit: 'm' },
      { key: 'bedDepth', label: 'Total bed depth', min: 0.4, max: 1.5, step: 0.05, unit: 'm' },
      { key: 'numVessels', label: 'Vessels', min: 1, max: 4, step: 1, unit: '' },
    ],
    advancedSliders: [
      { key: 'anthraciteDepth', label: 'Anthracite depth', min: 0.1, max: 1.0, step: 0.05, unit: 'm' },
      { key: 'sandDepth', label: 'Sand depth', min: 0.1, max: 0.8, step: 0.05, unit: 'm' },
      { key: 'garnetDepth', label: 'Garnet depth', min: 0.05, max: 0.5, step: 0.05, unit: 'm' },
      { key: 'anthraciteES', label: 'Anthracite ES', min: 0.8, max: 2.0, step: 0.1, unit: 'mm', info: 'Effective size d10 — coarser at top.' },
      { key: 'sandES', label: 'Sand ES', min: 0.3, max: 1.0, step: 0.05, unit: 'mm' },
      { key: 'garnetES', label: 'Garnet ES', min: 0.2, max: 0.6, step: 0.05, unit: 'mm' },
      { key: 'backwashRate', label: 'Backwash rate', min: 15, max: 60, step: 1, unit: 'm³/m²·h' },
    ],
    scienceNotes: [
      'Plain multimedia practical cut-off ≈ 10–20 µm (Cleasby & Logsdon, "Granular Bed and Precoat Filtration", AWWA). Particles smaller than this pass without coagulation chemistry to bridge them up.',
      'Ergun equation governs clean head loss: ΔP/L = 150·μ·u·(1−ε)²/(ε³·d²) + 1.75·ρ·u²·(1−ε)/(ε³·d). For typical multimedia clean ΔP ≈ 0.3–0.8 bar at 10 m/h.',
      'Approach velocity 7–15 m/h typical (USEPA Filtration Guidance); >15 m/h risks media migration into freeboard during backwash.',
      'Backwash 25 m³/m²·h water-only (or 12 m³/m²·h water + 60 Nm³/m²·h air scour) expands anthracite ~30% — required to release captured solids (Amirtharajah & Wetstein, JAWWA 1980).',
      'No dissolved-species removal: Fe²⁺, Mn²⁺, hardness, organics all pass through. For these you need aeration + catalytic media or RO/IX upstream.',
      'With upstream coagulant (PACl/FeCl₃), sub-µm particles flocculate to capturable size (~20–50 µm); TSS removal climbs from ~35% to ~75%.',
    ],
    simulate: (p, input, ctx) => {
      const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2) * p.numVessels;
      const velocity = input.flow / area; // m/h
      // Upstream coagulation detection — drives TSS / SDI / turbidity capture
      const upstreamCoag    = ctx?.upstreamKeys.includes('pacl_dosing') ?? false;
      const upstreamAerated = ctx?.upstreamKeys.some(k => k === 'aeration_column' || k === 'cascade_aerator') ?? false;

      // TSS removal — well-attested ranges (Cleasby/Logsdon, USEPA):
      //   Plain media + sub-10 µm influent: 25–40%
      //   Plain media + coarser influent:   45–60%
      //   With upstream coagulation:        65–85%
      const baseRemoval = upstreamCoag ? 0.78 : (input.tss > 25 ? 0.55 : 0.35);
      const bedDepthBonus = Math.min(0.10, (p.bedDepth - 0.6) * 0.10);
      const velocityPenalty = velocity > 15 ? (velocity - 15) * 0.02 : 0;
      const tssRemoval = Math.max(0.10, Math.min(0.90, baseRemoval + bedDepthBonus - velocityPenalty));

      // Iron — particulate (Fe³⁺ floc) captured, dissolved (Fe²⁺) passes.
      // Oxidised fraction proxied from upstream DO.
      const oxidisedFrac = input.doIn > 2 ? 0.95 : 0.20;
      const feFlocCaptureFrac = upstreamCoag ? 0.92 : 0.80;
      const feOut = input.fe * (oxidisedFrac * (1 - feFlocCaptureFrac) + (1 - oxidisedFrac));

      // Manganese — Mn²⁺ unaffected without catalyst; Mn³⁺/Mn⁴⁺ floc captured if aerated
      const mnFlocCaptureFrac = upstreamCoag ? 0.85 : 0.70;
      const mnOut = input.mn * (oxidisedFrac * (1 - mnFlocCaptureFrac) + (1 - oxidisedFrac));

      // SDI improvement — typical 1.0–1.5 unit drop without coag, 2.0–2.5 with
      const sdiDrop = upstreamCoag ? 2.2 : 1.2;
      const sdiOut = Math.max(2.8, input.sdi - sdiDrop); // floor 2.8 — granular media alone won't beat that

      // Turbidity removal scales with bed depth and coagulation
      const turbCoeff = upstreamCoag ? 0.10 : 0.40;
      const turbOut = Math.max(0.08, input.turbidity * turbCoeff);

      // TSS out
      const tssOut = Math.max(0.2, input.tss * (1 - tssRemoval));

      const warn = velocity > 15 ? ` ⚠ v >15 m/h — media migration risk` : '';
      const coagNote = upstreamCoag ? 'with coagulation' : (upstreamAerated ? 'with aeration upstream' : 'plain bed, no coagulation');
      return {
        out: { ...input, tss: tssOut, turbidity: turbOut, fe: feOut, mn: mnOut, sdi: sdiOut },
        notes: `v ${velocity.toFixed(1)} m/h · TSS rem ${(tssRemoval * 100).toFixed(0)}% · SDI ${input.sdi.toFixed(1)}→${sdiOut.toFixed(1)} · ${p.numVessels}× DN${(p.vesselDiameter * 1000).toFixed(0)} · ${coagNote}${warn}`,
        capexGBP: 14000 * p.numVessels + p.vesselDiameter * 3000 * p.numVessels,
        opexGBPperYear: 800 * p.numVessels + (upstreamCoag ? 0 : 0),
      };
    },
  },

  dual_media_filter: {
    key: 'dual_media_filter', name: 'Dual-Media Filter (anthracite + sand)', icon: Layers, color: '#eab308',
    description: '2-layer bed: coarse anthracite on top, finer silica sand below. Practical cut-off ≈ 15–25 µm. Cheaper than multimedia; less effective on fine particles.',
    defaultParams: { vesselDiameter: 1.4, numVessels: 2, anthraciteDepth: 0.6, sandDepth: 0.4, anthraciteES: 1.2, sandES: 0.55, backwashRate: 25, freeboard: 0.5 },
    sliders: [
      { key: 'vesselDiameter', label: 'Vessel Ø', min: 0.5, max: 3, step: 0.05, unit: 'm' },
      { key: 'anthraciteDepth', label: 'Anthracite depth', min: 0.2, max: 1.0, step: 0.05, unit: 'm' },
      { key: 'sandDepth', label: 'Sand depth', min: 0.2, max: 0.8, step: 0.05, unit: 'm' },
      { key: 'numVessels', label: 'Vessels', min: 1, max: 4, step: 1, unit: '' },
    ],
    advancedSliders: [
      { key: 'anthraciteES', label: 'Anthracite ES', min: 0.8, max: 2.0, step: 0.1, unit: 'mm', info: 'Effective size d10.' },
      { key: 'sandES', label: 'Sand ES', min: 0.3, max: 1.0, step: 0.05, unit: 'mm' },
      { key: 'backwashRate', label: 'Backwash rate', min: 15, max: 60, step: 1, unit: 'm³/m²·h' },
      { key: 'freeboard', label: 'Freeboard', min: 0.3, max: 1.5, step: 0.05, unit: 'm' },
    ],
    scienceNotes: [
      'Dual-media practical cut-off ~15–25 µm (vs ~10–20 µm for full multimedia with garnet). Sub-µm particles still pass without coagulation.',
      'Stratification preserved by Richardson-Zaki expansion: anthracite ρ ≈ 1500 kg/m³ stays above sand ρ ≈ 2650 kg/m³ during backwash.',
      'Approach velocity envelope 7–15 m/h (USEPA Filtration Guidance). Above 15 m/h media migration risk.',
      'Backwash 25 m³/m²·h water-only or 12 m³/m²·h water + 60 Nm³/m²·h air scour for ~30 % expansion.',
    ],
    simulate: (p, input, ctx) => {
      const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2) * p.numVessels;
      const velocity = input.flow / area;
      const upstreamCoag = ctx?.upstreamKeys.includes('pacl_dosing') ?? false;
      // Dual-media slightly less efficient than triple-layer multimedia:
      const baseRemoval = upstreamCoag ? 0.72 : (input.tss > 25 ? 0.50 : 0.30);
      const bedTotal = p.anthraciteDepth + p.sandDepth;
      const bedDepthBonus = Math.min(0.08, (bedTotal - 0.7) * 0.10);
      const velocityPenalty = velocity > 15 ? (velocity - 15) * 0.02 : 0;
      const tssRemoval = Math.max(0.10, Math.min(0.85, baseRemoval + bedDepthBonus - velocityPenalty));
      const oxidisedFrac = input.doIn > 2 ? 0.95 : 0.20;
      const feFlocCapture = upstreamCoag ? 0.88 : 0.75;
      const feOut = input.fe * (oxidisedFrac * (1 - feFlocCapture) + (1 - oxidisedFrac));
      const sdiDrop = upstreamCoag ? 1.9 : 1.0;
      const sdiOut = Math.max(3.0, input.sdi - sdiDrop);
      const turbOut = Math.max(0.10, input.turbidity * (upstreamCoag ? 0.15 : 0.45));
      const tssOut = Math.max(0.2, input.tss * (1 - tssRemoval));
      const warn = velocity > 15 ? ` ⚠ v >15 m/h — media migration risk` : '';
      return {
        out: { ...input, tss: tssOut, turbidity: turbOut, fe: feOut, mn: input.mn * (1 - oxidisedFrac * 0.65), sdi: sdiOut },
        notes: `v ${velocity.toFixed(1)} m/h · TSS rem ${(tssRemoval * 100).toFixed(0)}% · SDI ${input.sdi.toFixed(1)}→${sdiOut.toFixed(1)}${warn}`,
        capexGBP: 11000 * p.numVessels + p.vesselDiameter * 2600 * p.numVessels,
        opexGBPperYear: 650 * p.numVessels,
      };
    },
  },

  sand_filter_single: {
    key: 'sand_filter_single', name: 'Sand Filter (single-media)', icon: Layers, color: '#d97706',
    description: 'Simple silica sand bed. Lowest cost particulate filter. Cut-off ~30–50 µm without coagulation — only suitable for surface-water polishing or with strong coagulation.',
    defaultParams: { vesselDiameter: 1.4, numVessels: 2, sandDepth: 0.8, sandES: 0.55, backwashRate: 20, freeboard: 0.5 },
    sliders: [
      { key: 'vesselDiameter', label: 'Vessel Ø', min: 0.5, max: 3, step: 0.05, unit: 'm' },
      { key: 'sandDepth', label: 'Sand depth', min: 0.4, max: 1.4, step: 0.05, unit: 'm' },
      { key: 'sandES', label: 'Sand ES', min: 0.3, max: 1.2, step: 0.05, unit: 'mm' },
      { key: 'numVessels', label: 'Vessels', min: 1, max: 4, step: 1, unit: '' },
    ],
    advancedSliders: [
      { key: 'backwashRate', label: 'Backwash rate', min: 10, max: 40, step: 1, unit: 'm³/m²·h', info: 'Sand-only beds need less backwash flow than multimedia.' },
      { key: 'freeboard', label: 'Freeboard', min: 0.3, max: 1.5, step: 0.05, unit: 'm' },
    ],
    scienceNotes: [
      'Single-media sand is the simplest pressure/gravity filter. Cut-off ~30–50 µm without coagulation. Used widely in municipal pre-RO when source water is already low TSS.',
      'For Mylar-type borehole (1–3 µm particles), sand alone gives <20% TSS removal — needs coagulation pre-treatment or use a finer-graded multimedia/UF instead.',
      'Approach velocity envelope 5–12 m/h is conservative (USEPA Filtration Guidance); slow sand filters operate at 0.1–0.4 m/h with biofilm.',
      'Filter run-time limited by clean-bed pressure drop ramp; backwash at ΔP ≈ 1.5 bar above clean baseline.',
    ],
    simulate: (p, input, ctx) => {
      const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2) * p.numVessels;
      const velocity = input.flow / area;
      const upstreamCoag = ctx?.upstreamKeys.includes('pacl_dosing') ?? false;
      // Sand-only is the weakest particulate filter unless heavily coagulated.
      const baseRemoval = upstreamCoag ? 0.65 : (input.tss > 50 ? 0.40 : 0.20);
      const tssRemoval = Math.max(0.05, Math.min(0.80, baseRemoval - (velocity > 12 ? (velocity - 12) * 0.03 : 0)));
      const oxidisedFrac = input.doIn > 2 ? 0.95 : 0.20;
      const feFlocCapture = upstreamCoag ? 0.80 : 0.65;
      const feOut = input.fe * (oxidisedFrac * (1 - feFlocCapture) + (1 - oxidisedFrac));
      const sdiDrop = upstreamCoag ? 1.5 : 0.7;
      const sdiOut = Math.max(3.5, input.sdi - sdiDrop);
      const turbOut = Math.max(0.15, input.turbidity * (upstreamCoag ? 0.25 : 0.60));
      const tssOut = Math.max(0.3, input.tss * (1 - tssRemoval));
      return {
        out: { ...input, tss: tssOut, turbidity: turbOut, fe: feOut, mn: input.mn * (1 - oxidisedFrac * 0.5), sdi: sdiOut },
        notes: `v ${velocity.toFixed(1)} m/h · TSS rem ${(tssRemoval * 100).toFixed(0)}% · cheapest particulate filter ${upstreamCoag ? '(with coag)' : '(no coag — weak)'}`,
        capexGBP: 8000 * p.numVessels + p.vesselDiameter * 2200 * p.numVessels,
        opexGBPperYear: 500 * p.numVessels,
      };
    },
  },

  gac_filter: {
    key: 'gac_filter', name: 'GAC Filter (granular activated carbon)', icon: Layers, color: '#8b5cf6',
    description: 'Adsorption bed for organics (TOC, DBP precursors), free chlorine, taste & odour. MUST be installed before RO if chlorine is present — chlorine destroys polyamide membranes.',
    defaultParams: { vesselDiameter: 1.4, numVessels: 2, bedDepth: 1.2, ebctMin: 10, gacBulkDensity: 480, backwashRate: 30 },
    sliders: [
      { key: 'vesselDiameter', label: 'Vessel Ø', min: 0.5, max: 3, step: 0.05, unit: 'm' },
      { key: 'bedDepth', label: 'Bed depth', min: 0.6, max: 2.5, step: 0.1, unit: 'm' },
      { key: 'numVessels', label: 'Vessels', min: 1, max: 4, step: 1, unit: '' },
      { key: 'ebctMin', label: 'Design EBCT', min: 5, max: 30, step: 1, unit: 'min', info: 'Empty-bed contact time. 10 min minimum for chlorine removal, 15–20 min for TOC.' },
    ],
    advancedSliders: [
      { key: 'gacBulkDensity', label: 'GAC bulk density', min: 400, max: 600, step: 10, unit: 'kg/m³' },
      { key: 'backwashRate', label: 'Backwash rate', min: 20, max: 50, step: 1, unit: 'm³/m²·h' },
    ],
    scienceNotes: [
      'GAC adsorbs free chlorine almost instantly (catalytic reduction Cl₂ + C → 2HCl + CO₂). 1 kg GAC reduces ~5 kg Cl₂ over its life.',
      'TOC / DBP-precursor removal driven by EBCT: 10 min gives ~40 % TOC reduction; 20 min ~65 % (USEPA EPA-815-R-99-014).',
      'GAC must be replaced or regenerated when breakthrough occurs (typ. every 6–24 months on chlorinated water; 3–6 months on heavy TOC load).',
      'Does NOT remove dissolved metals, salts, hardness, particulates >cut-off — it is an adsorption filter, not a sieve. Place AFTER particulate filtration to avoid premature plugging.',
    ],
    simulate: (p, input, ctx) => {
      const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2) * p.numVessels;
      const velocity = input.flow / area;
      const bedVolume = area * p.bedDepth;
      const ebctActual = (bedVolume / input.flow) * 60; // min
      const hasUpstreamParticulate = ctx?.upstreamKeys.some(k => k === 'multimedia' || k === 'uf' || k === 'cartridge_polish' || k === 'dual_media_filter' || k === 'sand_filter_single') ?? false;
      // GAC barely touches TSS / turbidity / SDI / Fe / Mn — it's an adsorption stage.
      // It DOES remove free chlorine (set to 0) and TOC (we don't track TOC currently).
      const turbReductionFactor = hasUpstreamParticulate ? 0.85 : 0.7; // some incidental capture
      const ebctOK = ebctActual >= p.ebctMin;
      return {
        out: { ...input, turbidity: input.turbidity * turbReductionFactor },
        notes: `v ${velocity.toFixed(1)} m/h · EBCT ${ebctActual.toFixed(1)} min ${ebctOK ? '✓' : `⚠ <${p.ebctMin} min target`} · chlorine ✓ · TOC reduction ~${ebctActual >= 15 ? 65 : ebctActual >= 10 ? 40 : 20}%`,
        capexGBP: 15000 * p.numVessels + p.vesselDiameter * 3200 * p.numVessels + bedVolume * 1800 * p.gacBulkDensity / 480, // GAC media ~£1800/m³
        opexGBPperYear: bedVolume * 1800 * 0.5 + 1200 * p.numVessels, // media replacement ~50%/year + maintenance
      };
    },
  },

  cartridge_polish: {
    key: 'cartridge_polish', name: '5 µm Cartridge Polish', icon: Filter, color: '#94a3b8',
    description: 'Disposable pleated cartridges. Catches floc breakthrough; protects RO membranes from short-circuit events.',
    defaultParams: { micron: 5, housings: 1, cartridgesPerHousing: 6, changeOutMonths: 2 },
    advancedSliders: [
      { key: 'changeOutMonths', label: 'Change-out interval', min: 1, max: 12, step: 1, unit: 'months' },
    ],
    scienceNotes: [
      'Pleated PP/glass-fibre cartridges, nominal rating typically 1–10 µm.',
      'DP triggers replacement at ~1 bar above clean; service life depends on upstream turbidity & operator policy.',
      'Belt-and-braces ahead of RO — catches floc breakthrough on upstream catalytic regen events.',
    ],
    sliders: [
      { key: 'micron', label: 'Rating', min: 1, max: 50, step: 1, unit: 'µm' },
      { key: 'housings', label: 'Housings', min: 1, max: 4, step: 1, unit: '' },
      { key: 'cartridgesPerHousing', label: 'Cartridges/housing', min: 1, max: 12, step: 1, unit: '' },
    ],
    simulate: (p, input) => {
      const totalCartridges = p.housings * p.cartridgesPerHousing;
      return {
        out: {
          ...input,
          fe: input.fe * 0.9,
          mn: input.mn * 0.95,
          turbidity: Math.max(0.05, input.turbidity * 0.7),
          sdi: Math.max(0.5, input.sdi - 0.3),
          tss: Math.max(0.1, input.tss * 0.5),
        },
        notes: `${p.micron} µm pleated, ${totalCartridges} cartridges total`,
        capexGBP: 2500 * p.housings,
        opexGBPperYear: totalCartridges * 35 * 6, // change every 2 months
      };
    },
  },

  uv_disinfection: {
    key: 'uv_disinfection', name: 'UV Disinfection', icon: Zap, color: '#a855f7',
    description: 'UV-C inactivation of bacteria, viruses & protozoa. Place AFTER particulate removal (UF or cartridge polish).',
    defaultParams: { numLamps: 2, lampPowerW: 80, uvtPct: 92, targetDoseMJ: 40, lampType: 'low_pressure', uvEfficiency: 0.35, chamberDiameter: 0.25, chamberLength: 1.0, pipeDiameter: 80 },
    sliders: [
      { key: 'numLamps', label: 'Lamps', min: 1, max: 12, step: 1, unit: '' },
      { key: 'lampPowerW', label: 'Lamp power', min: 40, max: 2000, step: 10, unit: 'W' },
      { key: 'uvtPct', label: 'UVT @ 254 nm', min: 50, max: 99, step: 1, unit: '%' },
      { key: 'targetDoseMJ', label: 'Target dose', min: 20, max: 200, step: 5, unit: 'mJ/cm²' },
    ],
    advancedSliders: [
      { key: 'chamberDiameter', label: 'Chamber Ø', min: 0.1, max: 1.2, step: 0.05, unit: 'm' },
      { key: 'chamberLength', label: 'Chamber length', min: 0.4, max: 3, step: 0.1, unit: 'm' },
      { key: 'uvEfficiency', label: 'UV efficiency', min: 0.1, max: 0.5, step: 0.01, unit: '' },
      { key: 'pipeDiameter', label: 'Pipe Ø', min: 25, max: 300, step: 5, unit: 'mm' },
    ],
    advancedSelects: [
      { key: 'lampType', label: 'Lamp technology', options: [
        { value: 'low_pressure', label: 'Low pressure (LP, 254 nm)' },
        { value: 'amalgam_low_pressure', label: 'Amalgam LP (high-output)' },
        { value: 'medium_pressure', label: 'Medium pressure (polychromatic)' },
      ]},
    ],
    scienceNotes: [
      'USEPA UVDGM target: 40 mJ/cm² delivered dose gives ≥4-log Giardia & Cryptosporidium.',
      'Beer-Lambert: UV penetration drops exponentially with UVT% and chamber radius — clean upstream water (SDI <3) is mandatory.',
      'Lamps age; design with fouling factor ≈ 0.7 (i.e. design ≈ target × 1.4 to keep delivered above target at end-of-life).',
      'Does not change chemistry — Fe, Mn, SDI, turbidity unchanged. Only inactivates microbes.',
    ],
    simulate: (p, input) => {
      const r = calcUV({
        flowRate: input.flow, uvtPct: p.uvtPct, targetDoseMJ: p.targetDoseMJ,
        lampType: p.lampType, numLamps: p.numLamps, lampPowerW: p.lampPowerW,
        uvEfficiency: p.uvEfficiency, chamberDiameter: p.chamberDiameter,
        chamberLength: p.chamberLength, pipeDiameter: p.pipeDiameter,
      });
      return {
        // UV does NOT change water chemistry — pass everything through.
        out: { ...input },
        notes: `Dose ${r.deliveredDose.toFixed(1)} mJ/cm² (target ${p.targetDoseMJ}) · log inact. E.coli ${r.logInactE_coli.toFixed(1)} · Giardia ${r.logInactGiardia.toFixed(1)} · ${r.energyKWhPerM3.toFixed(3)} kWh/m³`,
        capexGBP: 12000 + p.numLamps * (p.lampType === 'medium_pressure' ? 4500 : p.lampType === 'amalgam_low_pressure' ? 2200 : 900),
        opexGBPperYear: p.numLamps * (p.lampType === 'medium_pressure' ? 850 : 450) + r.energyKWhPerM3 * input.flow * 8760 * 0.18,
      };
    },
  },

  pacl_dosing: {
    key: 'pacl_dosing', name: 'PACl Coagulant Dosing', icon: FlaskConical, color: '#f43f5e',
    description: 'Polyaluminium chloride flocculates sub-µm particles up to capturable size. Needed upstream of multimedia for borehole-fine waters.',
    defaultParams: { dose: 1.5, contactTime: 60, tankVolume: 200 },
    advancedSliders: [
      { key: 'tankVolume', label: 'Chemical tank', min: 50, max: 1000, step: 25, unit: 'L' },
    ],
    scienceNotes: [
      'PACl (Al₁₃) bridges colloidal particles; effective dose 0.5–2 mg/L as Al for low-turbidity boreholes.',
      'Optimal mixing at G ≈ 600 s⁻¹ for 30–60 s, then gentle flocculation at G ≈ 50 s⁻¹.',
      'Over-dose creates excess sludge; under-dose lets particles pass.',
    ],
    sliders: [
      { key: 'dose', label: 'PACl dose', min: 0.5, max: 10, step: 0.1, unit: 'mg/L as Al' },
      { key: 'contactTime', label: 'Mixing time', min: 15, max: 300, step: 5, unit: 's' },
    ],
    simulate: (p, input) => {
      // PACl flocs sub-µm particles. Increases turbidity slightly, drops SDI baseline assumption.
      return {
        out: { ...input, turbidity: input.turbidity + 0.2, sdi: Math.max(input.sdi, 5) },
        notes: `${p.dose.toFixed(1)} mg/L PACl, ${p.contactTime}s mixing — particles flocculated`,
        capexGBP: 4500,
        opexGBPperYear: (p.dose / 1000) * (input.flow * 24 * 365) * 1.2, // £1.2/kg PACl rough
      };
    },
  },

  desalination_daf: {
    key: 'desalination_daf', name: 'Desalination DAF (saline pre-treatment)', icon: Droplets, color: '#0891b2',
    description: 'Saline-water dissolved-air flotation ahead of SWRO/BWRO. Coagulation + micro-bubble flotation strips colloids, algae and oil → drops SDI into the RO design window.',
    defaultParams: { coagulantDose: 6, recycleRatio: 0.35, airSaturationPressure: 5, hydraulicLoad: 8 },
    sliders: [
      { key: 'coagulantDose',  label: 'Coagulant (FeCl₃)', min: 1, max: 20, step: 0.5, unit: 'mg/L' },
      { key: 'recycleRatio',   label: 'Recycle ratio',     min: 0.1, max: 0.6, step: 0.05, unit: '' },
      { key: 'hydraulicLoad',  label: 'Surface load',      min: 4, max: 16, step: 0.5, unit: 'm/h' },
    ],
    advancedSliders: [
      { key: 'airSaturationPressure', label: 'Saturator pressure', min: 3, max: 7, step: 0.1, unit: 'bar', info: 'Henry-law air dissolution; higher pressure = more bubbles released.' },
    ],
    scienceNotes: [
      'Henry\'s law: dissolved-air mass ∝ saturator pressure; released as 30–80 µm bubbles at the nozzle.',
      'Coagulation (FeCl₃) destabilises colloids so micro-bubbles can float them — the SDI driver, not bulk TSS.',
      'Edzwald (2010): DAF removes algae/colloids far better than sedimentation → ideal SWRO pre-treatment.',
    ],
    simulate: (p, input) => {
      const eff = Math.min(0.95, 0.70 + Math.min(0.18, p.coagulantDose * 0.018) + Math.min(0.07, p.recycleRatio * 0.12));
      const tssOut = Math.max(0.2, input.tss * (1 - eff));
      const turbOut = Math.max(0.1, input.turbidity * (1 - Math.min(0.93, eff)));
      const sdiOut = Math.max(2.5, input.sdi - (input.sdi - 2.5) * Math.min(0.9, eff));
      return {
        out: { ...input, tss: tssOut, turbidity: turbOut, sdi: sdiOut },
        notes: `Saline DAF — TSS ${(eff * 100).toFixed(0)}% removed · SDI ${input.sdi.toFixed(1)}→${sdiOut.toFixed(1)} · ${p.coagulantDose} mg/L FeCl₃`,
        capexGBP: 26000 + p.hydraulicLoad * 1800,
        opexGBPperYear: (p.coagulantDose / 1000) * (input.flow * 24 * 365) * 0.9 + p.airSaturationPressure * input.flow * 60,
      };
    },
  },

  ammonia_stripper: {
    key: 'ammonia_stripper', name: 'Ammonia Hot Stripper (VTA)', icon: Wind, color: '#16a34a',
    description: 'Counter-current air stripping of NH₃ at elevated pH/temperature. Domain note: this is a nitrogen-removal unit (more wastewater than RO pre-treatment) — modelled conservatively.',
    defaultParams: { ph: 10.5, temperature: 80, airToWaterRatio: 2500, packingHeight: 6 },
    sliders: [
      { key: 'ph',             label: 'Operating pH',  min: 8.5, max: 12, step: 0.1, unit: '' },
      { key: 'temperature',    label: 'Temperature',   min: 15, max: 90, step: 1, unit: '°C' },
      { key: 'airToWaterRatio',label: 'Air : Water',   min: 500, max: 6000, step: 100, unit: 'm³/m³' },
    ],
    advancedSliders: [
      { key: 'packingHeight', label: 'Packed height', min: 3, max: 12, step: 0.5, unit: 'm', info: 'More transfer units (NTU) → higher removal.' },
    ],
    scienceNotes: [
      'NH₃/NH₄⁺ equilibrium: un-ionised fraction f = 1/(1+10^(pKa−pH)); pKa ≈ 0.09018 + 2729.92/(T+273.15) (Emerson 1975).',
      'Only the free NH₃ fraction strips — hence high pH (≥10.5) and warmth are needed.',
      'Removal ≈ 1 − e^(−NTU); NTU scales with air:water ratio and packed height.',
    ],
    simulate: (p, input) => {
      const T = p.temperature;
      const pKa = 0.09018 + 2729.92 / (T + 273.15);
      const fNH3 = 1 / (1 + Math.pow(10, pKa - p.ph));
      const ntu = (p.airToWaterRatio / 1500) * (p.packingHeight / 6);
      const eff = Math.max(0, Math.min(0.97, fNH3 * (1 - Math.exp(-ntu))));
      const nh3In = input.nh3 ?? 0;
      return {
        out: { ...input, nh3: nh3In * (1 - eff) },
        notes: `Hot stripper ${T}°C · pH ${p.ph} — free-NH₃ ${(fNH3 * 100).toFixed(0)}% · removed ${(eff * 100).toFixed(0)}% (${nh3In.toFixed(1)}→${(nh3In * (1 - eff)).toFixed(1)} mg/L)`,
        capexGBP: 45000 + p.packingHeight * 4000,
        opexGBPperYear: Math.max(0, T - 12) * input.flow * 24 * 0.05 * 365 + p.airToWaterRatio * input.flow * 0.002 * 8760 / 1000,
      };
    },
  },

  electrochlorination: {
    key: 'electrochlorination', name: 'Electrochlorination (on-site NaOCl)', icon: Zap, color: '#eab308',
    description: 'In-situ sodium-hypochlorite generation from brine for disinfection. Adds a free-chlorine residual; negligible bulk-quality change. Dechlorinate before any RO membrane.',
    defaultParams: { dose: 2, cellCurrent: 100, brineStrength: 3 },
    sliders: [
      { key: 'dose',        label: 'Free-Cl dose', min: 0.5, max: 8, step: 0.1, unit: 'mg/L' },
      { key: 'cellCurrent', label: 'Cell current', min: 20, max: 400, step: 10, unit: 'A' },
    ],
    scienceNotes: [
      '2 NaCl + 2 H₂O → 2 NaOCl + H₂ ; ~3–4 kWh per kg equivalent chlorine produced.',
      'Free chlorine destroys polyamide RO membranes within hours above ~0.1 mg/L — always dechlorinate (SMBS/GAC) upstream of RO.',
    ],
    simulate: (p, input) => ({
      out: { ...input, chlorineResidual: p.dose },
      notes: `On-site NaOCl — ${p.dose.toFixed(1)} mg/L free Cl dosed (disinfection). ⚠ Dechlorinate before RO.`,
      capexGBP: 22000,
      opexGBPperYear: p.cellCurrent * 0.0035 * 8760 * 0.18 + 1500,
    }),
  },

  orifice: {
    key: 'orifice', name: 'Orifice Plate (flow / pressure control)', icon: CircleDot, color: '#64748b',
    description: 'Inline orifice for flow metering / pressure reduction. A hydraulic device — no treatment effect; reports pressure drop and cavitation margin.',
    defaultParams: { boreDiameter: 50, pipeDiameter: 100 },
    sliders: [
      { key: 'boreDiameter', label: 'Bore Ø', min: 5, max: 150, step: 1, unit: 'mm' },
      { key: 'pipeDiameter', label: 'Pipe Ø', min: 25, max: 300, step: 5, unit: 'mm' },
    ],
    scienceNotes: [
      'ΔP ≈ ½ρv²/Cd² with discharge coefficient Cd ≈ 0.61 for a sharp-edged orifice.',
      'β = bore/pipe diameter ratio; cavitation risk rises as ΔP approaches the upstream-minus-vapour pressure.',
    ],
    simulate: (p, input) => {
      const beta = p.boreDiameter / Math.max(1, p.pipeDiameter);
      const Q = input.flow / 3600;                                  // m³/s
      const A = Math.PI * Math.pow(p.boreDiameter / 1000, 2) / 4;   // m²
      const Cd = 0.61;
      const v = Q / Math.max(1e-6, A * Cd);
      const dPbar = (0.5 * 1000 * v * v) / 1e5;
      return {
        out: { ...input },
        notes: `Orifice β=${beta.toFixed(2)} — ΔP ≈ ${dPbar.toFixed(2)} bar · v ${v.toFixed(1)} m/s (flow control; no treatment effect)`,
        capexGBP: 1200,
        opexGBPperYear: 0,
      };
    },
  },

  /* ─── Wastewater process units ──────────────────────────────
     Live cascade driven by the validated removal models in
     processTrainSimulator.ts (Metcalf & Eddy, Lawrence-McCarty, Stokes,
     Henry, Freundlich). Final discharge compliance is checked on the
     Process Train validation page. */
  screening: wwDef('screening', 'Screening (bar screen)', Filter, '#94a3b8',
    'Coarse/fine bar screen — removes rags, grit & large solids. First wastewater pre-treatment unit.',
    9000, 12, 1.5, ['Coarse 6 mm / fine 1–3 mm aperture. TSS 15–30%, BOD 5–15% (particle-bound).']),

  eq: wwDef('eq', 'Equalization Tank', Container, '#64748b',
    'Dampens diurnal flow & load peaks; gives quiescent settling. Stabilises downstream biology.',
    14000, 45, 1.2, ['Peak:average BOD load ratio reduced ~60%. ~8% settleable solids removed passively.']),

  ph: wwDef('ph', 'pH Adjustment', FlaskConical, '#f43f5e',
    'Acid/caustic/lime dosing to the biological window (6.5–9.0). High pH also precipitates heavy-metal hydroxides.',
    16000, 30, 4, ['NaOH/H₂SO₄/lime to target ~7.2. Above pH 9: M²⁺ + 2OH⁻ → M(OH)₂↓ co-precipitation (75–85%).']),

  buffer: wwDef('buffer', 'Buffer Tank', Container, '#475569',
    'Surge protection / process stability between stages. No pollutant removal.',
    11000, 35, 0.5, ['Hydraulic buffering only — protects downstream units from shock loads.']),

  daf: wwDef('daf', 'DAF (Dissolved Air Flotation)', Droplets, '#06b6d4',
    'Coagulation + micro-bubble flotation. Strong on O&G and TSS; chemical P co-removal with coagulant.',
    26000, 120, 22, [
      "Henry's law: air solubility ≈105 mg/L at 6 bar; 20–100 µm bubbles, rise velocity 0.5–5 m/h (Stokes).",
      'TSS 65–95%, O&G 82–98%. Alum/polymer coagulation drives charge neutralisation for high FOG/TSS.']),

  lamella: wwDef('lamella', 'Lamella Clarifier', Layers, '#0ea5e9',
    'Inclined-plate gravity settler — 3–5× the footprint-equivalent settling area of a flat tank.',
    22000, 90, 6, [
      "Stokes' Law: vₛ = (ρ_p−ρ_w)·g·d_p²/18µ. 60° plates multiply surface 3–5×.",
      'SOR 0.5–1.5 m/h (plate-equivalent). TSS 65–80%.']),

  aeration: wwDef('aeration', 'Aeration Tank (Activated Sludge)', Wind, '#22c55e',
    'Suspended-growth biological oxidation. BOD/COD removal + nitrification at SRT ≥10 d. Needs a downstream clarifier.',
    40000, 180, 38, [
      'Lawrence-McCarty (1970): Se = Ks(1+kd·θc)/[θc(µmax−kd)−1]. SRT 12 d, HRT 8 h typical.',
      'O₂ demand 1.0 kg O₂/kg BOD + 4.57 kg O₂/kg NH₃-N nitrified. Nitrification needs pH 6.5–8.5, SRT >8–15 d.']),

  secondary: wwDef('secondary', 'Secondary Clarifier', Layers, '#14b8a6',
    'Separates activated-sludge biomass; RAS returns to aeration, WAS controls SRT.',
    24000, 95, 7, [
      'SOR 0.8–1.5 m/h; SVI target 80–150 mL/g. TSS ~90% (MLSS settling).']),

  mbbr: wwDef('mbbr', 'MBBR (Moving Bed Biofilm Reactor)', Recycle, '#10b981',
    'Biofilm on suspended plastic carriers — high effective SRT in a compact tank. Good for nitrification; needs downstream solids separation.',
    52000, 160, 30, [
      'Biofilm carriers (50–60% fill). Loading ≤2 g NH₃-N/m²·d for full nitrification. No sludge recycle.',
      'Sloughed biofilm needs a downstream clarifier or DAF.']),

  uasb: wwDef('uasb', 'UASB (Anaerobic Reactor)', Waves, '#84cc16',
    'Upflow anaerobic granular sludge bed for high-strength waste. Converts organics to biogas — net energy producer.',
    60000, 150, 12, [
      'Granular sludge, HLR 1–3 m/h, HRT 4–8 h. ≈0.35 m³ CH₄/kg COD removed.',
      'Best for BOD > 500 mg/L. Effluent needs aerobic polishing; NH₃-N is released, not removed.']),

  mbr: wwDef('mbr', 'MBR (Membrane Bioreactor)', Microscope, '#0d9488',
    'Activated sludge + immersed UF membrane — near-zero TSS permeate, no secondary clarifier needed.',
    90000, 320, 60, [
      'UF membrane 0.04–0.4 µm retains all biomass: permeate TSS <1 mg/L, turbidity <0.2 NTU, BOD <5, COD <30.',
      'HRT 3–6 h, SRT 15–30 d, TMP 0.2–0.5 bar. Hard barrier independent of settleability.']),

  gac: wwDef('gac', 'GAC Adsorption (tertiary)', Filter, '#7c3aed',
    'Granular activated carbon polishing — adsorbs refractory COD, colour, phenol & surfactants. Needs low upstream TSS.',
    30000, 110, 24, [
      'Freundlich isotherm q_e = K_f·C_e^(1/n). EBCT ~15 min. Breakthrough at 50% inlet triggers regeneration.',
      'Pre-filter to TSS <10 mg/L or the bed fouls fast.']),

  sludge: wwDef('sludge', 'Sludge Holding', Trash, '#a16207',
    'Sidestream thickened-sludge storage. No effect on the main water-quality train; reject water may need treatment.',
    18000, 40, 5, ['Consolidates primary + secondary sludge. Supernatant return can be high in NH₃-N and TP.']),
};

/* ─── Helpers ───────────────────────────────────────────── */

function defaultWater(): Water {
  return {
    flow: 24, fe: 0.47, mn: 0.05, ph: 7.8, alkalinity: 84, tss: 1,
    turbidity: 0.35, sdi: 5.0, doIn: 2, temp: 12, hardness: 205, flowDay: 576, nh3: 0,
  };
}

const fmt = (v: number, d = 2) => (Number.isFinite(v) ? v : 0).toFixed(d);

function newStage(key: EquipmentKey): StageConfig {
  return {
    id: `st_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    key,
    params: { ...EQUIPMENT_LIBRARY[key].defaultParams },
    enabled: true,
  };
}

const RECOMMENDED_PRESET: EquipmentKey[] = ['aeration_column', 'greensand', 'cartridge_polish'];

/** Map a saved equipmentList item to an EquipmentKey. Prefers the stored `key`
 *  (lossless round-trip); falls back to matching the saved unit name for legacy
 *  trains that only stored a name. Returns 'multimedia' for anything unknown. */
function mapItemToKey(it: any): EquipmentKey {
  if (it?.key && (EQUIPMENT_LIBRARY as Record<string, EquipmentDef>)[it.key]) return it.key as EquipmentKey;
  const n = String(it?.name || '').toLowerCase();
  // Wastewater units (legacy name-only items) — checked before water-treatment.
  if (n.includes('screen')) return 'screening';
  if (n.includes('equaliz') || n.includes('eq tank')) return 'eq';
  if (n.includes('membrane bioreactor') || /\bmbr\b/.test(n)) return 'mbr';
  if (n.includes('mbbr') || n.includes('moving bed')) return 'mbbr';
  if (n.includes('uasb') || n.includes('anaerobic')) return 'uasb';
  if (n.includes('daf') || n.includes('flotation')) return 'daf';
  if (n.includes('lamella')) return 'lamella';
  if (n.includes('secondary clar')) return 'secondary';
  if (n.includes('activated sludge') || n.includes('aeration tank')) return 'aeration';
  if (n.includes('buffer')) return 'buffer';
  if (n.includes('sludge')) return 'sludge';
  if (n.includes('neutralis') || n.includes('neutraliz') || n.includes('ph adjust')) return 'ph';
  if (n.includes('cascade')) return 'cascade_aerator';
  if (n.includes('aeration')) return 'aeration_column';
  if (n.includes('greensand') || n.includes('catalytic') || n.includes('birm') || n.includes('pyrolus')) return 'greensand';
  if (n.includes('desal')) return 'desalination_daf';
  if (n.includes('ammonia') || n.includes('stripper')) return 'ammonia_stripper';
  if (n.includes('electrochlor') || n.includes('hypochlor')) return 'electrochlorination';
  if (n.includes('orifice')) return 'orifice';
  if (n.includes('ultrafilt') || /\buf\b/.test(n)) return 'uf';
  if (n.includes('dual')) return 'dual_media_filter';
  if (n.includes('sand')) return 'sand_filter_single';
  if (n.includes('gac') || n.includes('carbon')) return 'gac_filter';
  if (n.includes('uv')) return 'uv_disinfection';
  if (n.includes('cartridge') || n.includes('polish')) return 'cartridge_polish';
  if (n.includes('pacl') || n.includes('coagul')) return 'pacl_dosing';
  return 'multimedia';
}

/* ─── Compact Slider ─── */
function ESlider({ label, value, min, max, step, unit, color = '#06b6d4', onChange }: {
  label: string; value: number; min: number; max: number; step: number;
  unit: string; color?: string; onChange: (v: number) => void;
}) {
  const v = Number.isFinite(value) ? value : 0;
  const display = step < 0.01 ? v.toFixed(3) : step < 0.1 ? v.toFixed(2) : step < 1 ? v.toFixed(1) : v.toFixed(0);
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium text-slate-400">{label}</span>
        <span className="text-[11px] font-bold text-white tabular-nums">
          {display}<span className="text-[9px] text-slate-500 font-normal ml-0.5">{unit}</span>
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={v} onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full cursor-pointer rb-segmented"
        style={{ accentColor: color, color, ['--p' as any]: `${Math.max(0, Math.min(100, ((v - min) / Math.max(0.0001, (max - min))) * 100)).toFixed(2)}%` }} />
    </div>
  );
}

/* ─── Page ─── */
export default function TrainEditPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('id');
  const presetCustomerId = params.get('customer_id') ? Number(params.get('customer_id')) : null;

  const [trainName, setTrainName] = useState('New Process Train');
  const [trainDescription, setTrainDescription] = useState('');
  const [influent, setInfluent] = useState<Water>(defaultWater());
  const [stages, setStages] = useState<StageConfig[]>([]);
  const [linkedSampleId, setLinkedSampleId] = useState<number | null>(null);
  const [linkedCustomerId, setLinkedCustomerId] = useState<number | null>(presetCustomerId);
  const [linkedSiteId, setLinkedSiteId] = useState<number | null>(null);
  const [samplePickerOpen, setSamplePickerOpen] = useState(false);
  const [equipmentPickerOpen, setEquipmentPickerOpen] = useState(false);
  const [detailStageId, setDetailStageId] = useState<string | null>(null);
  // Insert popup context — when set, opens a contextual picker that
  // pre-classifies each equipment as suggested / allowed / blocked.
  const [insertContext, setInsertContext] = useState<{ idx: number; prevKey: EquipmentKey | null; nextKey: EquipmentKey | null } | null>(null);

  // RO membrane selection — drives the life expectancy estimate shown in the RO Feed card
  const [roMembraneId, setRoMembraneId] = useState<string>(() => {
    try { return localStorage.getItem('rb_train_ro_membrane') || 'hyd_espa2_ld'; }
    catch { return 'hyd_espa2_ld'; }
  });
  const [roElementsPerVessel, setRoElementsPerVessel] = useState<number>(6);
  useEffect(() => { try { localStorage.setItem('rb_train_ro_membrane', roMembraneId); } catch { /* */ } }, [roMembraneId]);
  const [saving, setSaving] = useState(false);
  const [showInfluentPanel, setShowInfluentPanel] = useState(true);
  const [samplesList, setSamplesList] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);

  // Preset handoff — when arriving from Mylar option cards (or any future
  // page) localStorage may contain a `rb_train_preset_pending` key with name,
  // description, influent and a list of {key, params} stages. Consume once.
  useEffect(() => {
    if (editId) return; // editing existing — ignore preset
    try {
      const raw = localStorage.getItem('rb_train_preset_pending');
      if (!raw) return;
      const preset = JSON.parse(raw);
      localStorage.removeItem('rb_train_preset_pending');
      if (preset.name) setTrainName(preset.name);
      if (preset.description) setTrainDescription(preset.description);
      if (preset.influent) {
        const p = preset.influent;
        const wq = rawParamsToWQ(p);
        setInfluent(prev => ({
          ...prev,
          flow: p.flowRate ? p.flowRate / 24 : prev.flow,
          fe: p.fe ?? p.iron ?? prev.fe,
          mn: p.mn ?? p.manganese ?? prev.mn,
          ph: p.ph ?? prev.ph,
          alkalinity: p.alkalinity ?? prev.alkalinity,
          tss: p.tss ?? prev.tss,
          turbidity: p.turbidity ?? prev.turbidity,
          sdi: p.sdi ?? prev.sdi,
          temp: p.temp ?? p.temperature ?? prev.temp,
          hardness: p.hardness ?? prev.hardness,
          nh3: p.nh3 ?? p.nh3n ?? wq.ammoniaN ?? prev.nh3,
          bod5: wq.bod5, cod: wq.cod, oilGrease: wq.oilGrease, totalNitrogen: wq.totalNitrogen,
          totalPhosphorus: wq.totalPhosphorus, tds: wq.tds, conductivity: wq.conductivity,
          color: wq.color, sulfides: wq.sulfides, surfactants: wq.surfactants, phenol: wq.phenol,
          lead: wq.lead, mercury: wq.mercury, cadmium: wq.cadmium, chromium: wq.chromium,
          nickel: wq.nickel, zinc: wq.zinc, copper: wq.copper,
        }));
      }
      if (Array.isArray(preset.stages)) {
        const built: StageConfig[] = preset.stages.map((s: any) => ({
          id: `st_${Math.random().toString(36).slice(2, 10)}`,
          key: s.key as EquipmentKey,
          params: { ...(EQUIPMENT_LIBRARY[s.key as EquipmentKey]?.defaultParams ?? {}), ...(s.params ?? {}) },
          enabled: true,
        }));
        setStages(built);
      }
      toast.success(`Preloaded "${preset.name ?? 'preset'}" — ${preset.stages?.length ?? 0} stage${preset.stages?.length === 1 ? '' : 's'}`);
    } catch (e: any) {
      console.error('preset hydration failed', e);
    }
  }, [editId]);

  // True once the influent has been hydrated (from a saved snapshot or the
  // linked sample) so the auto-sample effect doesn't clobber a restore.
  const influentHydratedRef = useRef(false);

  // Load existing train if editing — local store first (the Customers trains
  // tab reads the same local store), then the PHP backend as a fallback.
  useEffect(() => {
    if (!editId) return;
    const apply = (t: any) => {
      if (!t) { toast.error(`Train id=${editId} not found`); return; }
      setTrainName(t.name || 'Train');
      setTrainDescription(t.description || '');
      setLinkedCustomerId(t.customerId != null ? Number(t.customerId) : null);
      setLinkedSampleId(t.sampleId != null ? Number(t.sampleId) : null);
      // Rebuild stages from equipmentList — map by stored key (lossless),
      // restoring each stage's saved params.
      if (Array.isArray(t.equipmentList)) {
        const rebuilt: StageConfig[] = t.equipmentList.map((it: any) => {
          const key = mapItemToKey(it);
          return {
            id: `st_${Math.random().toString(36).slice(2, 10)}`,
            key,
            params: { ...EQUIPMENT_LIBRARY[key].defaultParams, ...(it.params ?? {}) },
            enabled: true,
          };
        });
        setStages(rebuilt);
        const approximated = t.equipmentList.filter((it: any) =>
          !(it?.key && (EQUIPMENT_LIBRARY as Record<string, EquipmentDef>)[it.key])
          && mapItemToKey(it) === 'multimedia'
          && !String(it?.name || '').toLowerCase().includes('multimedia'),
        ).length;
        if (approximated > 0) {
          toast(`${approximated} unit(s) approximated — not in the water-treatment editor library`, { icon: '⚠️', duration: 6000 });
        }
      }
      // Restore the exact influent snapshot if the train carries one.
      const snap = t.assessmentResult?.influentSnapshot;
      if (snap && typeof snap === 'object') {
        setInfluent(prev => ({ ...prev, ...snap }));
        influentHydratedRef.current = true;
      }
    };
    try {
      const local = getLocalProcessTrains().find(x => String(x.id) === String(editId));
      if (local) { apply(local); return; }
    } catch { /* ignore local read */ }
    fetch(`/api/process-trains/list.php`, { credentials: 'include', headers: { Authorization: `Bearer ${localStorage.getItem('rb_auth_token') || ''}` } })
      .then(r => r.json())
      .then((data: any) => {
        const trains = Array.isArray(data) ? data : (data.trains || []);
        apply(trains.find((x: any) => String(x.id) === editId));
      })
      .catch(e => toast.error(`Load failed: ${e?.message ?? e}`));
  }, [editId]);

  // Load sample list scoped to customer (if any) — merges the local influent
  // store (what the Customers / Influent pages save) with the PHP backend.
  useEffect(() => {
    const local = (() => {
      try {
        const all = getLocalInfluentSamples();
        return linkedCustomerId != null
          ? all.filter((s: any) => Number(s.customerId) === Number(linkedCustomerId))
          : all;
      } catch { return []; }
    })();
    apiListInfluentSamples(linkedCustomerId ?? undefined)
      .then((s: any) => {
        const server = Array.isArray(s) ? s : (s.samples || []);
        const byId = new Map<string, any>();
        [...local, ...server].forEach((x: any) => byId.set(String(x.id), x));
        setSamplesList(Array.from(byId.values()));
      })
      .catch(() => setSamplesList(local));
  }, [linkedCustomerId]);

  useEffect(() => {
    apiListCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  // Recompute flowDay when flow changes
  useEffect(() => { setInfluent(p => ({ ...p, flowDay: p.flow * 24 })); }, [influent.flow]);

  /* Live cascade simulation — passes upstream chain context to each stage so
     equipment can adapt physics to what came before (e.g. multimedia uses
     coagulation presence; UF uses upstream DO for Fe²⁺/Fe³⁺ split). */
  const cascadeResult = useMemo(() => {
    const out: StageResult[] = [];
    let current: Water = { ...influent };
    let totalCapex = 0;
    let totalOpex = 0;
    const upstreamKeys: EquipmentKey[] = [];
    for (const stage of stages) {
      if (!stage.enabled) continue;
      const def = EQUIPMENT_LIBRARY[stage.key];
      const sim = def.simulate(stage.params, current, { upstreamKeys: [...upstreamKeys] });
      out.push({ in: { ...current }, out: sim.out, notes: sim.notes, capexGBP: sim.capexGBP, opexGBPperYear: sim.opexGBPperYear });
      totalCapex += sim.capexGBP;
      totalOpex += sim.opexGBPperYear;
      current = sim.out;
      upstreamKeys.push(stage.key);
    }
    return { stageResults: out, finalWater: current, totalCapex, totalOpex };
  }, [influent, stages]);

  const meetsRO = useMemo(() => ({
    fe: cascadeResult.finalWater.fe <= RO_TARGETS.fe,
    mn: cascadeResult.finalWater.mn <= 0.05,
    sdi: cascadeResult.finalWater.sdi <= RO_TARGETS.sdi,
    tss: cascadeResult.finalWater.tss <= RO_TARGETS.tss,
  }), [cascadeResult]);
  const allPass = meetsRO.fe && meetsRO.mn && meetsRO.sdi && meetsRO.tss;

  /* Wastewater train? — true if it uses any WW process unit or the influent
     carries an organic/nutrient load. Drives the "discharge compliance on the
     Process Train page" note (the RO design basis is a water-treatment metric). */
  const isWastewaterTrain = useMemo(() => {
    if (stages.some(s => s.enabled && WW_KEYS.has(s.key))) return true;
    const w = influent;
    return (w.cod ?? 0) > 0 || (w.bod5 ?? 0) > 0 || (w.oilGrease ?? 0) > 0;
  }, [stages, influent]);

  /* Equipment management */
  const addStage = useCallback((key: EquipmentKey) => {
    setStages(prev => [...prev, newStage(key)]);
    setEquipmentPickerOpen(false);
    toast.success(`Added ${EQUIPMENT_LIBRARY[key].name}`);
  }, []);

  const removeStage = (id: string) => setStages(prev => prev.filter(s => s.id !== id));
  const moveStage = (id: string, dir: -1 | 1) => {
    setStages(prev => {
      const idx = prev.findIndex(s => s.id === id);
      if (idx < 0) return prev;
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };
  const updateStageParam = (id: string, paramKey: string, value: any) => {
    setStages(prev => prev.map(s => s.id === id ? { ...s, params: { ...s.params, [paramKey]: value } } : s));
  };
  const toggleStage = (id: string) => setStages(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));

  /* ─── Smart insert logic ──────────────────────────────────────────────
     Decide which equipment makes sense to insert at a given position based
     on upstream / downstream chain. Returns one of:
       - 'suggested' (highlighted green): the recommended next stage
       - 'allowed'   (neutral): physically OK to add
       - 'blocked'   (red, disabled): nonsense combination; surfaces a reason
     Rules — high-level:
       • PACl dosing only before particulate filtration (multimedia/UF), never
         after UF/UV/cartridge.
       • UV must be downstream of particulate filtration (UF or cartridge);
         otherwise particles shadow the microbes.
       • Greensand needs DO — preferred only after aeration.
       • Multimedia after UF or UV degrades the work upstream.
       • Two consecutive aerations are redundant.
       • Cartridge polish is fine almost anywhere; final-before-RO ideal.
  */
  type Suggestion = { state: 'suggested' | 'allowed' | 'blocked'; reason?: string };
  const evaluateInsertion = (
    candidate: EquipmentKey,
    prevKey: EquipmentKey | null,
    nextKey: EquipmentKey | null,
    chain: EquipmentKey[]
  ): Suggestion => {
    const hasUpstreamAeration = chain.some(k => k === 'aeration_column' || k === 'cascade_aerator');
    const hasUpstreamParticulate = chain.some(k => k === 'multimedia' || k === 'uf' || k === 'cartridge_polish');

    // Block list first
    if (candidate === 'pacl_dosing') {
      if (prevKey === 'uf' || prevKey === 'uv_disinfection' || prevKey === 'cartridge_polish')
        return { state: 'blocked', reason: 'PACl chemicals must be added BEFORE fine filtration, not after.' };
    }
    if (candidate === 'uv_disinfection') {
      if (!hasUpstreamParticulate)
        return { state: 'blocked', reason: 'UV needs clean upstream water (UF or cartridge polish first) — particles shadow microbes.' };
    }
    if ((candidate === 'multimedia' || candidate === 'dual_media_filter' || candidate === 'sand_filter_single')
        && (prevKey === 'uf' || prevKey === 'uv_disinfection')) {
      return { state: 'blocked', reason: 'Granular media downstream of UF/UV degrades quality already achieved.' };
    }
    if (candidate === 'gac_filter' && (prevKey === 'uf' || prevKey === 'uv_disinfection')) {
      return { state: 'blocked', reason: 'GAC after UF/UV is uncommon — pre-RO chlorine removal should be before particulate barrier.' };
    }
    if ((candidate === 'aeration_column' || candidate === 'cascade_aerator') &&
        (prevKey === 'aeration_column' || prevKey === 'cascade_aerator')) {
      return { state: 'blocked', reason: 'Two consecutive aeration stages — redundant.' };
    }

    // Suggestions
    if (prevKey === 'pacl_dosing' && (candidate === 'multimedia' || candidate === 'dual_media_filter'))
      return { state: 'suggested', reason: 'PACl flocs are sized for granular-media capture.' };
    if (candidate === 'gac_filter' && (prevKey === 'multimedia' || prevKey === 'dual_media_filter' || prevKey === 'cartridge_polish'))
      return { state: 'suggested', reason: 'GAC after particulate stage — removes chlorine and organics before RO.' };
    if ((prevKey === 'aeration_column' || prevKey === 'cascade_aerator') && candidate === 'greensand')
      return { state: 'suggested', reason: 'Aeration → catalytic Fe/Mn media is the canonical sequence.' };
    if (prevKey === 'greensand' && candidate === 'cartridge_polish')
      return { state: 'suggested', reason: 'Cartridge polish catches Fe³⁺ floc breakthrough from catalytic stage.' };
    if (prevKey === 'greensand' && candidate === 'uf')
      return { state: 'suggested', reason: 'UF after catalytic media → guaranteed SDI <3 for RO.' };
    if (prevKey === 'cartridge_polish' && candidate === 'uf')
      return { state: 'suggested', reason: 'UF guarantees SDI <3 — final RO insurance.' };
    if ((prevKey === 'uf' || prevKey === 'cartridge_polish') && candidate === 'uv_disinfection')
      return { state: 'suggested', reason: 'UV polishing after particulate removal — microbe inactivation.' };
    if (candidate === 'uf' && !hasUpstreamAeration && (prevKey === null || (prevKey !== 'greensand' && prevKey !== 'cartridge_polish' && prevKey !== 'multimedia')))
      return { state: 'allowed', reason: 'Will work, but fouls fast if dissolved Fe is present — add aeration + catalytic upstream first.' };
    if (candidate === 'greensand' && !hasUpstreamAeration)
      return { state: 'allowed', reason: 'Works but needs DO — consider aeration upstream first.' };

    return { state: 'allowed' };
  };

  const insertStageAt = (key: EquipmentKey, idx: number) => {
    setStages(prev => {
      const next = [...prev];
      next.splice(idx, 0, newStage(key));
      return next;
    });
    setInsertContext(null);
    toast.success(`Inserted ${EQUIPMENT_LIBRARY[key].name} at position ${idx + 1}`);
  };

  // Swap an aeration stage between column and cascade tower (preserves position)
  const swapAerationType = (id: string) => {
    setStages(prev => prev.map(s => {
      if (s.id !== id) return s;
      if (s.key !== 'aeration_column' && s.key !== 'cascade_aerator') return s;
      const newKey: EquipmentKey = s.key === 'aeration_column' ? 'cascade_aerator' : 'aeration_column';
      const oldName = EQUIPMENT_LIBRARY[s.key].name;
      const newName = EQUIPMENT_LIBRARY[newKey].name;
      toast.success(`Swapped: ${oldName} → ${newName}`);
      return { ...s, key: newKey, params: { ...EQUIPMENT_LIBRARY[newKey].defaultParams } };
    }));
  };

  const loadRecommended = () => {
    setStages(RECOMMENDED_PRESET.map(k => newStage(k)));
    toast.success('Loaded Recommended preset (Aeration → Greensand → Cartridge polish)');
  };

  /* Auto-size every stage's geometry against the current influent flow so all
     velocities land in the design envelope. Conservative defaults; user can
     fine-tune in the detail popup afterwards. */
  const autoConfigureForCurrentWater = () => {
    const Q = Math.max(1, influent.flow); // m³/h
    setStages(prev => prev.map(s => {
      const p = { ...s.params };
      switch (s.key) {
        case 'aeration_column': {
          // Target superficial velocity 30 m/h
          const targetArea = Q / 30;
          p.columnDiameter = Math.max(0.3, Math.min(2.5, 2 * Math.sqrt(targetArea / Math.PI)));
          p.columnHeight = Q < 30 ? 2.5 : Q < 100 ? 3.5 : 4.5;
          p.airToWaterRatio = Math.max(5, Math.min(15, 8));
          break;
        }
        case 'cascade_aerator': {
          p.flowRate = Q;
          p.towerDiameter = Math.max(0.6, Math.min(3, Math.sqrt(Q / 8)));
          p.stepCount = Q < 30 ? 4 : Q < 100 ? 5 : 6;
          break;
        }
        case 'greensand': {
          // Target service velocity 8 m/h
          const targetArea = Q / 8;
          const dutyVessels = p.arrangement === 'duplex_duty_standby' ? 1 : 2;
          p.vesselDiameter = Math.max(0.6, Math.min(2.4, 2 * Math.sqrt(targetArea / Math.PI / dutyVessels)));
          p.bedDepth = 0.9;
          break;
        }
        case 'multimedia':
        case 'dual_media_filter':
        case 'sand_filter_single': {
          // Target 10 m/h
          const targetArea = Q / 10;
          const dutyVessels = Math.max(2, Math.round(p.numVessels || 2));
          p.numVessels = dutyVessels;
          p.vesselDiameter = Math.max(0.6, Math.min(2.4, 2 * Math.sqrt(targetArea / Math.PI / dutyVessels)));
          break;
        }
        case 'gac_filter': {
          // Target EBCT 10 min minimum
          const bedVol = Q * (p.ebctMin || 10) / 60; // m³
          const dutyVessels = Math.max(1, p.numVessels || 2);
          p.numVessels = dutyVessels;
          const target = bedVol / (p.bedDepth || 1.2) / dutyVessels;
          p.vesselDiameter = Math.max(0.6, Math.min(2.4, 2 * Math.sqrt(target / Math.PI)));
          break;
        }
        case 'uf': {
          // Target 60 LMH flux → area = Q × 1000 / 60 m²
          const area = (Q * 1000) / 60;
          p.numModules = Math.max(1, Math.ceil(area / (p.moduleArea || 50)));
          break;
        }
        case 'cartridge_polish': {
          // Aim for 1 housing per ~30 m³/h
          p.housings = Math.max(1, Math.ceil(Q / 30));
          break;
        }
        case 'pacl_dosing': {
          p.dose = Math.max(0.5, Math.min(2.5, influent.tss > 5 ? 1.8 : 1.2));
          break;
        }
        case 'uv_disinfection': {
          // Size for target 40 mJ/cm² @ current UVT — increase lamps if flow high
          p.numLamps = Math.max(2, Math.ceil(Q / 12));
          break;
        }
      }
      return { ...s, params: p };
    }));
    toast.success('Auto-sized every stage against current influent water and flow');
  };

  const loadFromSample = (sample: any) => {
    const p = sample.params || {};
    const wq = rawParamsToWQ(p); // wastewater quality (0 for absent params)
    setInfluent(prev => ({
      ...prev,
      flow: p.flowRate ? p.flowRate / 24 : prev.flow,
      fe: p.fe ?? p.iron ?? prev.fe,
      mn: p.mn ?? p.manganese ?? prev.mn,
      ph: p.ph ?? prev.ph,
      alkalinity: p.alkalinity ?? prev.alkalinity,
      tss: p.tss ?? prev.tss,
      turbidity: p.turbidity ?? prev.turbidity,
      sdi: p.sdi ?? prev.sdi,
      temp: p.temp ?? p.temperature ?? prev.temp,
      hardness: p.hardness ?? prev.hardness,
      nh3: p.nh3 ?? p.nh3n ?? p.ammonia ?? p.nh4 ?? wq.ammoniaN ?? prev.nh3,
      // Wastewater quality — carried so WW process units cascade live.
      bod5: wq.bod5, cod: wq.cod, oilGrease: wq.oilGrease, totalNitrogen: wq.totalNitrogen,
      totalPhosphorus: wq.totalPhosphorus, tds: wq.tds, conductivity: wq.conductivity,
      color: wq.color, sulfides: wq.sulfides, surfactants: wq.surfactants, phenol: wq.phenol,
      lead: wq.lead, mercury: wq.mercury, cadmium: wq.cadmium, chromium: wq.chromium,
      nickel: wq.nickel, zinc: wq.zinc, copper: wq.copper,
    }));
    setLinkedSampleId(sample.id);
    setLinkedCustomerId(sample.customerId ?? null);
    setSamplePickerOpen(false);
    toast.success(`Loaded ${sample.sampleName || 'sample'}`);
  };

  // Auto-hydrate the influent from the train's linked sample once the sample
  // list resolves (unless an exact influent snapshot was already restored).
  useEffect(() => {
    if (influentHydratedRef.current) return;
    if (linkedSampleId == null || samplesList.length === 0) return;
    const found = samplesList.find((s: any) => Number(s.id) === Number(linkedSampleId));
    if (found) { loadFromSample(found); influentHydratedRef.current = true; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedSampleId, samplesList]);

  /* Save */
  const handleSave = async () => {
    if (!trainName.trim()) { toast.error('Train name required'); return; }
    setSaving(true);
    const enabled = stages.filter(s => s.enabled);
    // Persist the stage `key` (lossless reload) alongside the human-readable
    // name and per-stage params.
    const equipmentList = cascadeResult.stageResults.map((r, i) => ({
      order: i + 1,
      key: enabled[i]?.key,
      name: EQUIPMENT_LIBRARY[enabled[i]?.key as EquipmentKey]?.name ?? 'Stage',
      feIn: r.in.fe, feOut: r.out.fe,
      mnIn: r.in.mn, mnOut: r.out.mn,
      sdiIn: r.in.sdi, sdiOut: r.out.sdi,
      turbIn: r.in.turbidity, turbOut: r.out.turbidity,
      notes: r.notes,
      meetsRoTarget: r.out.fe <= RO_TARGETS.fe && r.out.sdi <= RO_TARGETS.sdi,
      params: enabled[i]?.params,
    }));
    const assessmentResult = {
      finalFe: cascadeResult.finalWater.fe,
      finalMn: cascadeResult.finalWater.mn,
      finalSdi: cascadeResult.finalWater.sdi,
      finalTurb: cascadeResult.finalWater.turbidity,
      meetsRoTarget: allPass,
      capexEstimateGBP: cascadeResult.totalCapex,
      opexAnnualGBP: cascadeResult.totalOpex,
      influentSnapshot: influent,
      stagesCount: enabled.length,
    };
    const base = {
      name: trainName, description: trainDescription || null,
      sampleId: linkedSampleId, customerId: linkedCustomerId,
      equipmentList, status: 'assessed' as const, assessmentResult,
    };

    // 1) Local mirror — keeps the Customers trains tab in sync and lets the
    //    feature work even without the PHP backend.
    let localId: number | undefined = editId ? Number(editId) : undefined;
    try {
      const existsLocally = !!editId && getLocalProcessTrains().some(t => String(t.id) === String(editId));
      if (existsLocally) updateLocalProcessTrain(Number(editId), base as any);
      else localId = saveLocalProcessTrain({ ...(editId ? { id: Number(editId) } : {}), ...base } as any).id;
    } catch { /* ignore local write */ }

    // 2) PHP backend — best effort.
    try {
      const payload: any = { ...base, siteId: linkedSiteId };
      if (editId) payload.id = Number(editId);
      const token = localStorage.getItem('rb_auth_token') || '';
      const url = token
        ? `/api/process-trains/save.php?_token=${encodeURIComponent(token)}`
        : '/api/process-trains/save.php';
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(token ? { ...payload, __token: token } : payload),
      });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.text()).slice(0, 300); } catch {/* ignore */}
        throw new Error(`HTTP ${res.status} ${detail}`);
      }
      const data = await res.json();
      toast.success(editId ? `Train updated (id=${data.id})` : `Train created (id=${data.id})`);
      if (!editId && data.id) navigate(`/train-edit?id=${data.id}`, { replace: true });
    } catch {
      // PHP unavailable — the local mirror above still persisted the change.
      toast.success(editId ? 'Train updated (saved locally)' : 'Train created (saved locally)');
      if (!editId && localId) navigate(`/train-edit?id=${localId}`, { replace: true });
    } finally {
      setSaving(false);
    }
  };

  /* ─── Render ─── */
  return (
    <div className="space-y-5 px-3 py-4">
      <PageHeader
        title={editId ? `Edit Train · ${trainName}` : 'New Process Train'}
        subtitle="Build and simulate an equipment chain end-to-end. Live cascade outputs and RO-target compliance."
        icon={Workflow}
        actions={
          <div className="flex gap-2">
            <button onClick={loadRecommended}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-amber-500/10 text-amber-400 text-xs font-bold border border-amber-500/20 hover:bg-amber-500/20 transition-all">
              <Sparkles className="w-3.5 h-3.5" />Recommended preset
            </button>
            <button onClick={autoConfigureForCurrentWater}
              disabled={stages.length === 0}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-cyan-500/10 text-cyan-400 text-xs font-bold border border-cyan-500/20 hover:bg-cyan-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              ⚙ Auto-configure
            </button>
            <button
              onClick={() => {
                if (stages.length === 0) { toast.error('Add at least one stage first'); return; }
                try {
                  const enabledStages = stages.filter(s => s.enabled);
                  const reportData: TrainReportInput = {
                    trainName,
                    description: trainDescription,
                    customerName: customers.find((c: any) => Number(c.id) === linkedCustomerId)?.company,
                    preparedBy: 'Reynolds & Bauhm Ltd · Smart Tool 2.0',
                    generatedAt: new Date().toISOString(),
                    influent,
                    stages: enabledStages.map((s, i) => {
                      const def = EQUIPMENT_LIBRARY[s.key];
                      const stageResult = cascadeResult.stageResults[i];
                      return {
                        order: i + 1,
                        key: s.key,
                        name: def.name,
                        description: def.description,
                        paramsSummary: Object.entries(s.params).map(([k, v]) => ({
                          label: k,
                          value: typeof v === 'number' ? v.toFixed(2).replace(/\.00$/, '') : String(v),
                        })),
                        inWater: stageResult?.in as any,
                        outWater: stageResult?.out as any,
                        notes: stageResult?.notes ?? '',
                        capexGBP: stageResult?.capexGBP ?? 0,
                        opexGBPperYear: stageResult?.opexGBPperYear ?? 0,
                        physics: (def.scienceNotes ?? []).map(n => ({ label: 'Engineering note', detail: n })),
                        validation: validateStage(s, stageResult?.in ?? influent),
                      };
                    }),
                    finalWater: cascadeResult.finalWater,
                    totalCapexGBP: cascadeResult.totalCapex,
                    totalOpexAnnualGBP: cascadeResult.totalOpex,
                    roMembrane: RO_MEMBRANES.find(m => m.id === roMembraneId) ?? RO_MEMBRANES[0],
                    roLifeYears: estimateROLife(
                      { fe: cascadeResult.finalWater.fe, mn: cascadeResult.finalWater.mn, sdi: cascadeResult.finalWater.sdi, tss: cascadeResult.finalWater.tss, turbidity: cascadeResult.finalWater.turbidity, temp: cascadeResult.finalWater.temp, freeChlorine: 0 },
                      RO_MEMBRANES.find(m => m.id === roMembraneId) ?? RO_MEMBRANES[0],
                      roElementsPerVessel
                    ).adjustedYears,
                    roCost10yrGBP: estimateROLife(
                      { fe: cascadeResult.finalWater.fe, mn: cascadeResult.finalWater.mn, sdi: cascadeResult.finalWater.sdi, tss: cascadeResult.finalWater.tss, turbidity: cascadeResult.finalWater.turbidity, temp: cascadeResult.finalWater.temp, freeChlorine: 0 },
                      RO_MEMBRANES.find(m => m.id === roMembraneId) ?? RO_MEMBRANES[0],
                      roElementsPerVessel
                    ).cost10yrGBP,
                    roElementsPerVessel,
                    meetsRO: allPass,
                  };
                  const doc = generateTrainPdfReport(reportData);
                  const safeName = trainName.replace(/[^a-z0-9-]+/gi, '_').slice(0, 80);
                  doc.save(`RB-TrainReport-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
                  toast.success('PDF report generated');
                } catch (e: any) {
                  console.error(e);
                  toast.error(`PDF failed: ${e?.message ?? e}`);
                }
              }}
              disabled={stages.length === 0}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-rose-500/10 text-rose-300 text-xs font-bold border border-rose-500/20 hover:bg-rose-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              📄 PDF report
            </button>
            <button onClick={handleSave} disabled={saving || stages.length === 0}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              <Save className="w-3.5 h-3.5" />{saving ? 'Saving…' : editId ? 'Save changes' : 'Save train'}
            </button>
          </div>
        }
      />

      <div className="mb-2 text-center">
        <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
          Reynolds &amp; Bauhm Smart Tool 2.0 — Train Designer — All Rights Reserved
        </p>
      </div>

      {/* TOP META BAR */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5 p-3 rb-card space-y-1.5">
          <label className="text-[10px] text-slate-500 uppercase">Train name</label>
          <input value={trainName} onChange={e => setTrainName(e.target.value)}
            className="w-full bg-rb-elevated border border-slate-800 rounded-lg px-3 py-1.5 text-sm font-bold text-white" />
          <input value={trainDescription} onChange={e => setTrainDescription(e.target.value)} placeholder="Description (optional)"
            className="w-full bg-rb-elevated border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-300 placeholder-slate-600" />
        </div>
        <div className="md:col-span-4 p-3 rb-card space-y-1.5">
          <label className="text-[10px] text-slate-500 uppercase">Customer</label>
          <select value={linkedCustomerId ?? ''} onChange={e => setLinkedCustomerId(e.target.value ? Number(e.target.value) : null)}
            className="w-full bg-rb-elevated border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200">
            <option value="">— (none) —</option>
            {customers.map((c: any) => (
              <option key={c.id} value={c.id}>{c.company || c.name}</option>
            ))}
          </select>
          <button onClick={() => setSamplePickerOpen(true)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all">
            <Database className="w-3.5 h-3.5" />{linkedSampleId ? `Sample #${linkedSampleId} loaded` : 'Pick influent sample…'}
          </button>
        </div>
        <div className="md:col-span-3 p-3 rb-card">
          <p className="text-[10px] text-slate-500 uppercase mb-1">RO compliance</p>
          <div className={`p-2 rounded-lg border ${allPass ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'}`}>
            <div className="flex items-center gap-2">
              {allPass ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <ShieldAlert className="w-4 h-4 text-rose-400" />}
              <span className={`text-xs font-bold uppercase ${allPass ? 'text-emerald-400' : 'text-rose-400'}`}>
                {allPass ? 'PASS' : 'FAIL'}
              </span>
            </div>
            <div className="text-[10px] mt-1 grid grid-cols-4 gap-1">
              {(['fe','mn','sdi','tss'] as const).map(k => (
                <div key={k} className="text-center">
                  <p className="text-slate-500 uppercase text-[9px]">{k}</p>
                  <p className={meetsRO[k] ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>
                    {meetsRO[k] ? '✓' : '✗'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* INFLUENT PANEL */}
      <div className="rb-card">
        <button onClick={() => setShowInfluentPanel(s => !s)}
          className="w-full flex items-center justify-between p-3 hover:bg-slate-800/30 transition-colors">
          <div className="flex items-center gap-2">
            <Droplets className="w-4 h-4 text-sky-400" />
            <span className="text-sm font-bold text-white">Influent water</span>
            <span className="text-[10px] text-slate-500">
              {fmt(influent.flow, 1)} m³/h · Fe {fmt(influent.fe, 3)} · pH {fmt(influent.ph, 1)} · SDI {fmt(influent.sdi, 1)}
            </span>
          </div>
          {showInfluentPanel ? <EyeOff className="w-3.5 h-3.5 text-slate-500" /> : <Eye className="w-3.5 h-3.5 text-slate-500" />}
        </button>
        {showInfluentPanel && (
          <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <ESlider label="Flow"        value={influent.flow}       min={1}   max={300}  step={1}    unit="m³/h"        color="#38bdf8" onChange={v => setInfluent({...influent, flow: v})} />
            <ESlider label="Fe (Fe²⁺)"   value={influent.fe}         min={0}   max={5}    step={0.01} unit="mg/L"        color="#f43f5e" onChange={v => setInfluent({...influent, fe: v})} />
            <ESlider label="Mn (Mn²⁺)"   value={influent.mn}         min={0}   max={2}    step={0.01} unit="mg/L"        color="#a855f7" onChange={v => setInfluent({...influent, mn: v})} />
            <ESlider label="pH"          value={influent.ph}         min={5}   max={9.5}  step={0.1}  unit=""            color="#a855f7" onChange={v => setInfluent({...influent, ph: v})} />
            <ESlider label="Alkalinity"  value={influent.alkalinity} min={10}  max={500}  step={5}    unit="mg/L CaCO₃"  color="#10b981" onChange={v => setInfluent({...influent, alkalinity: v})} />
            <ESlider label="Hardness"    value={influent.hardness}   min={10}  max={1000} step={5}    unit="mg/L CaCO₃"  color="#10b981" onChange={v => setInfluent({...influent, hardness: v})} />
            <ESlider label="TSS"         value={influent.tss}        min={0}   max={500}  step={1}    unit="mg/L"        color="#fbbf24" onChange={v => setInfluent({...influent, tss: v})} />
            <ESlider label="Turbidity"   value={influent.turbidity}  min={0.01} max={100} step={0.05} unit="NTU"         color="#fbbf24" onChange={v => setInfluent({...influent, turbidity: v})} />
            <ESlider label="SDI (base)"  value={influent.sdi}        min={1}   max={6}    step={0.1}  unit=""            color="#fbbf24" onChange={v => setInfluent({...influent, sdi: v})} />
            <ESlider label="DO in"       value={influent.doIn}       min={0}   max={12}   step={0.1}  unit="mg/L"        color="#06b6d4" onChange={v => setInfluent({...influent, doIn: v})} />
            <ESlider label="Temperature" value={influent.temp}       min={2}   max={30}   step={1}    unit="°C"          color="#06b6d4" onChange={v => setInfluent({...influent, temp: v})} />
          </div>
        )}
      </div>

      {/* PROCESS FLOW DIAGRAM */}
      {stages.length > 0 && (
        <div className="rb-card p-4">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-bold mb-2 flex items-center gap-2">
            <ArrowRight className="w-3.5 h-3.5" /> Process Flow (live simulation)
          </p>
          <ProcessDiagram stages={stages} cascade={cascadeResult.stageResults} influent={influent} />
        </div>
      )}

      {/* STAGES */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Equipment chain ({stages.filter(s => s.enabled).length} active stage{stages.filter(s => s.enabled).length === 1 ? '' : 's'})</h3>
          <button onClick={() => setEquipmentPickerOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all">
            <Plus className="w-3.5 h-3.5" />Add stage
          </button>
        </div>

        {stages.length === 0 && (
          <div className="rb-card p-8 text-center">
            <Workflow className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No stages yet. Click "Add stage" or "Recommended preset" to get started.</p>
          </div>
        )}

        {stages.length > 0 && (
          <InsertBetween onClick={() => setInsertContext({ idx: 0, prevKey: null, nextKey: stages[0].key })} />
        )}
        {stages.map((stage, idx) => {
          const def = EQUIPMENT_LIBRARY[stage.key];
          const Icon = def.icon;
          const result = cascadeResult.stageResults[stages.slice(0, idx + 1).filter(s => s.enabled).length - 1];
          const isLast = idx === stages.length - 1;
          return (
            <div key={stage.id}>
            <div className={`rb-card p-4 space-y-3 border-l-4 ${stage.enabled ? '' : 'opacity-40'}`}
              style={{ borderLeftColor: def.color }}>
              {/* Header */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {/* Live/off status pulse */}
                  <div className="shrink-0 relative w-3 h-3" title={stage.enabled ? 'LIVE — included in simulation' : 'OFF — bypassed'}>
                    {stage.enabled ? (
                      <>
                        <span className="absolute inset-0 rounded-full bg-sky-400 animate-ping opacity-75" />
                        <span className="absolute inset-0 rounded-full bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]" />
                      </>
                    ) : (
                      <>
                        <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-60" />
                        <span className="absolute inset-0 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.7)]" />
                      </>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                       style={{ backgroundColor: def.color + '22', border: `1px solid ${def.color}55` }}>
                    <Icon className="w-4 h-4" style={{ color: def.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate flex items-center gap-2">
                      {idx + 1}. {def.name}
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${stage.enabled ? 'text-sky-300 bg-sky-500/15' : 'text-rose-300 bg-rose-500/15'}`}>
                        {stage.enabled ? 'LIVE' : 'OFF'}
                      </span>
                    </p>
                    <p className="text-[10px] text-slate-500 truncate">{def.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {(stage.key === 'aeration_column' || stage.key === 'cascade_aerator') && (
                    <button onClick={() => swapAerationType(stage.id)}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all"
                      title={`Swap to ${stage.key === 'aeration_column' ? 'Cascade Aerator (gravity tower)' : 'Aeration Column (pressurised packed bed)'}`}>
                      ⇄ Swap to {stage.key === 'aeration_column' ? 'Cascade' : 'Column'}
                    </button>
                  )}
                  <button onClick={() => setDetailStageId(stage.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 transition-all"
                    title="Open full configuration popup">
                    🔧 Configure in detail
                  </button>
                  <button onClick={() => moveStage(stage.id, -1)} disabled={idx === 0}
                    className="p-1.5 rounded text-slate-400 hover:bg-slate-800/50 disabled:opacity-30">
                    <MoveUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => moveStage(stage.id, 1)} disabled={isLast}
                    className="p-1.5 rounded text-slate-400 hover:bg-slate-800/50 disabled:opacity-30">
                    <MoveDown className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => toggleStage(stage.id)}
                    className="p-1.5 rounded text-slate-400 hover:bg-slate-800/50" title={stage.enabled ? 'Disable' : 'Enable'}>
                    {stage.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => removeStage(stage.id)}
                    className="p-1.5 rounded text-rose-400 hover:bg-rose-500/20">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Sliders + selects */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {(def.selectFields || []).map(f => (
                  <div key={f.key} className="space-y-1">
                    <label className="text-[10px] text-slate-500 uppercase">{f.label}</label>
                    <select value={stage.params[f.key]} onChange={e => updateStageParam(stage.id, f.key, e.target.value)}
                      className="w-full bg-rb-elevated border border-slate-800 rounded-lg px-2 py-1 text-[11px] text-slate-200">
                      {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                ))}
                {def.sliders.map(s => (
                  <ESlider key={s.key} label={s.label} value={stage.params[s.key]} min={s.min} max={s.max} step={s.step} unit={s.unit}
                    color={def.color} onChange={v => updateStageParam(stage.id, s.key, v)} />
                ))}
              </div>

              {/* Live outputs */}
              {stage.enabled && result && (
                <StageOutputBar result={result} color={def.color} />
              )}
              {/* Physics validation chips */}
              {stage.enabled && result && (
                <ValidationChips checks={validateStage(stage, result.in)} />
              )}
            </div>
            {/* Inline insert button after each stage */}
            <InsertBetween onClick={() => setInsertContext({ idx: idx + 1, prevKey: stage.key, nextKey: stages[idx + 1]?.key ?? null })} />
            </div>
          );
        })}
      </div>

      {/* FINAL OUTCOME → RO FEED */}
      {stages.filter(s => s.enabled).length > 0 && (() => {
        const membrane = RO_MEMBRANES.find(m => m.id === roMembraneId) ?? RO_MEMBRANES[0];
        const roLife = estimateROLife(
          {
            fe: cascadeResult.finalWater.fe,
            mn: cascadeResult.finalWater.mn,
            sdi: cascadeResult.finalWater.sdi,
            tss: cascadeResult.finalWater.tss,
            turbidity: cascadeResult.finalWater.turbidity,
            temp: cascadeResult.finalWater.temp,
            freeChlorine: 0,
          },
          membrane,
          roElementsPerVessel,
        );
        const lifeRating: 'good' | 'fair' | 'poor' | 'dead' =
          roLife.chlorineKill ? 'dead' :
          roLife.adjustedYears >= membrane.nominalLifeYears * 0.85 ? 'good' :
          roLife.adjustedYears >= membrane.nominalLifeYears * 0.6  ? 'fair' :
          'poor';
        const lifeColour =
          lifeRating === 'good' ? '#10b981' :
          lifeRating === 'fair' ? '#fbbf24' :
          lifeRating === 'poor' ? '#fb923c' :
          '#f43f5e';
        return (
      <div className={`rb-card p-4 border-l-4 ${allPass ? 'border-emerald-500/60' : 'border-rose-500/60'}`}>
          <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
            <Gauge className="w-4 h-4 text-cyan-400" /> RO Feed Specification
          </h3>
          <p className="text-[11px] text-slate-500 mb-3">
            Cumulative outcome of <span className="text-slate-300 font-semibold">every enabled stage above</span> (including any aeration tower / cascade you added). This is the water that will enter your downstream RO membrane bank — life expectancy below depends on it.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-3">
            {[
              { label: 'Final Fe',  value: cascadeResult.finalWater.fe,  unit: 'mg/L', target: RO_TARGETS.fe, pass: meetsRO.fe },
              { label: 'Final Mn',  value: cascadeResult.finalWater.mn,  unit: 'mg/L', target: 0.05,           pass: meetsRO.mn },
              { label: 'Final SDI', value: cascadeResult.finalWater.sdi, unit: '',     target: RO_TARGETS.sdi, pass: meetsRO.sdi },
              { label: 'Final TSS', value: cascadeResult.finalWater.tss, unit: 'mg/L', target: RO_TARGETS.tss, pass: meetsRO.tss },
              { label: 'Final Turbidity', value: cascadeResult.finalWater.turbidity, unit: 'NTU', target: 1, pass: cascadeResult.finalWater.turbidity <= 1 },
              { label: 'Capex (est)', value: cascadeResult.totalCapex / 1000, unit: 'k£' },
              { label: 'Opex / yr',   value: cascadeResult.totalOpex / 1000, unit: 'k£' },
            ].map((kpi, i) => (
              <div key={i} className="p-2 rb-inner-card">
                <p className="text-[10px] text-slate-500 uppercase">{kpi.label}</p>
                <p className={`text-base font-bold tabular-nums ${
                  kpi.pass === true ? 'text-emerald-400' :
                  kpi.pass === false ? 'text-rose-400' :
                  'text-white'
                }`}>{kpi.unit === 'k£' ? `£${kpi.value.toFixed(0)}k` : fmt(kpi.value, kpi.unit === 'mg/L' ? 3 : kpi.unit === 'NTU' ? 2 : 1)} {kpi.unit !== 'k£' && kpi.unit}</p>
                {kpi.target != null && (
                  <p className="text-[9px] text-slate-600">target ≤ {kpi.target}{kpi.unit}</p>
                )}
              </div>
            ))}
          </div>
          {/* Wastewater final effluent — discharge compliance is validated on the Process Train page */}
          {isWastewaterTrain && (() => {
            const fw = cascadeResult.finalWater;
            const ww: [string, keyof Water, number][] = [
              ['COD', 'cod', 0], ['BOD₅', 'bod5', 1], ['TSS', 'tss', 1], ['NH₃-N', 'nh3', 1],
              ['Total N', 'totalNitrogen', 1], ['Total P', 'totalPhosphorus', 2], ['Oil & Grease', 'oilGrease', 1],
            ].filter(([, k]) => ((fw[k as keyof Water] as number) ?? 0) > 0 || ((influent[k as keyof Water] as number) ?? 0) > 0) as [string, keyof Water, number][];
            return (
              <div className="p-3 rounded-lg bg-sky-500/10 border border-sky-500/30 mb-2">
                <div className="flex items-center gap-2 mb-2">
                  <Droplets className="w-4 h-4 text-sky-400" />
                  <span className="text-xs font-bold text-sky-300 uppercase">Wastewater final effluent (live cascade)</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {ww.map(([l, k, d]) => (
                    <div key={l} className="text-[11px]">
                      <span className="text-slate-500">{l}: </span>
                      <span className="text-slate-500 tabular-nums">{((influent[k] as number) ?? 0).toFixed(d)}</span>
                      <ArrowRight className="inline w-3 h-3 mx-1 text-slate-600" />
                      <span className="text-sky-200 font-bold tabular-nums">{((fw[k] as number) ?? 0).toFixed(d)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-2">
                  Each stage's effluent is the next stage's influent. <span className="text-sky-300 font-semibold">Discharge compliance vs the active standard is checked on the Process Train validation page</span> — the RO design basis below applies only if this train feeds an RO bank.
                </p>
              </div>
            );
          })()}
          {!isWastewaterTrain && !allPass && (
            <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/30">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-rose-400" />
                <span className="text-xs font-bold text-rose-400 uppercase">Effluent OUTSIDE RO design basis</span>
              </div>
              <p className="text-[10px] text-rose-400 mt-1 leading-relaxed">
                {!meetsRO.fe && `Fe ${cascadeResult.finalWater.fe.toFixed(3)} mg/L exceeds RO target of ${RO_TARGETS.fe} mg/L. `}
                {!meetsRO.mn && `Mn ${cascadeResult.finalWater.mn.toFixed(3)} mg/L too high. `}
                {!meetsRO.sdi && `SDI ${cascadeResult.finalWater.sdi.toFixed(1)} exceeds RO target ≤${RO_TARGETS.sdi}. `}
                {!meetsRO.tss && `TSS ${cascadeResult.finalWater.tss.toFixed(2)} mg/L exceeds RO target ≤${RO_TARGETS.tss} mg/L. `}
                Add or strengthen upstream stages.
              </p>
            </div>
          )}
          {!isWastewaterTrain && allPass && (
            <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-bold text-emerald-400 uppercase">Effluent meets RO design basis ✓</span>
              </div>
              <p className="text-[10px] text-emerald-400 mt-1">All four RO targets are within spec. RO membranes will see feed water within their design envelope.</p>
            </div>
          )}

          {/* RO MEMBRANE & LIFE EXPECTANCY */}
          <div className="mt-4 pt-4 border-t border-slate-800 space-y-3">
            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
              <CircleDot className="w-3.5 h-3.5 text-purple-400" />
              Downstream RO Membrane — life expectancy on this feed
            </h4>

            {/* Membrane selector */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="text-[10px] text-slate-500 uppercase">Membrane element</label>
                <select value={roMembraneId} onChange={e => setRoMembraneId(e.target.value)}
                  className="w-full mt-1 bg-rb-elevated border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200">
                  {RO_MEMBRANES.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.brand} · {m.model} — {m.classLabel} ({m.saltRejectionPct}% rej · {m.nominalLifeYears}y nom. life · £{m.elementPriceGBP}/elem)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Elements per vessel</label>
                <input type="number" min={1} max={8} value={roElementsPerVessel}
                  onChange={e => setRoElementsPerVessel(Math.max(1, Math.min(8, Number(e.target.value) || 6)))}
                  className="w-full mt-1 bg-rb-elevated border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200" />
              </div>
            </div>

            {/* Life expectancy card */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-1 p-3 rb-inner-card text-center" style={{ borderColor: lifeColour + '44', borderWidth: 2, borderStyle: 'solid' }}>
                <p className="text-[10px] text-slate-500 uppercase">Estimated life</p>
                <p className="text-3xl font-bold tabular-nums" style={{ color: lifeColour }}>
                  {roLife.adjustedYears < 1 ? `${(roLife.adjustedYears * 12).toFixed(0)}m` : `${roLife.adjustedYears.toFixed(1)}y`}
                </p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">vs nominal {membrane.nominalLifeYears}y</p>
                <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                  lifeRating === 'good' ? 'bg-emerald-500/15 text-emerald-400' :
                  lifeRating === 'fair' ? 'bg-amber-500/15 text-amber-400' :
                  lifeRating === 'poor' ? 'bg-orange-500/15 text-orange-400' :
                  'bg-rose-500/15 text-rose-400 animate-pulse'
                }`}>
                  {lifeRating === 'good' ? '✓ Good' : lifeRating === 'fair' ? '⚠ Fair' : lifeRating === 'poor' ? '⚠ Poor' : '✗ Membrane kill'}
                </span>
              </div>
              <div className="md:col-span-3 p-3 rb-inner-card space-y-2">
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  {[
                    { label: 'Fe penalty',   value: roLife.fePenaltyYears, unit: 'y lost' },
                    { label: 'Mn penalty',   value: roLife.mnPenaltyYears, unit: 'y lost' },
                    { label: 'SDI penalty',  value: roLife.sdiPenaltyYears, unit: 'y lost' },
                    { label: 'Temp penalty', value: roLife.tempPenaltyYears, unit: 'y lost' },
                  ].map(p => (
                    <div key={p.label} className="flex justify-between">
                      <span className="text-slate-500">{p.label}</span>
                      <span className={p.value > 0.1 ? 'text-amber-400 font-bold tabular-nums' : 'text-slate-600 tabular-nums'}>
                        {p.value.toFixed(2)} {p.unit}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-slate-800 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Elements / vessel</span>
                    <span className="text-slate-300 tabular-nums">{roElementsPerVessel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">£/replacement cycle</span>
                    <span className="text-amber-400 font-bold tabular-nums">£{roLife.replacementCostGBPperCycle.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Cycles in 10 yr</span>
                    <span className="text-slate-300 tabular-nums">{(10 / roLife.adjustedYears).toFixed(1)}×</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">10-yr membrane £</span>
                    <span className="text-amber-400 font-bold tabular-nums">£{Math.round(roLife.cost10yrGBP).toLocaleString()}</span>
                  </div>
                </div>
                {roLife.warnings.length > 0 && (
                  <div className="pt-2 border-t border-slate-800 space-y-1">
                    {roLife.warnings.map((w, i) => (
                      <p key={i} className="text-[10px] text-rose-400 leading-relaxed flex gap-1.5">
                        <span>•</span><span>{w}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Membrane envelope summary */}
            <div className="p-2 rb-inner-card grid grid-cols-2 md:grid-cols-6 gap-2 text-[10px]">
              <div className="text-slate-500">Element specs:</div>
              <div className="text-slate-300">Fe ≤ <span className="tabular-nums">{membrane.feMaxMgL}</span></div>
              <div className="text-slate-300">Mn ≤ <span className="tabular-nums">{membrane.mnMaxMgL}</span></div>
              <div className="text-slate-300">SDI ≤ <span className="tabular-nums">{membrane.sdiMax}</span></div>
              <div className="text-slate-300">free Cl ≤ <span className="tabular-nums">{membrane.freeChlorineMaxMgL}</span></div>
              <div className="text-slate-300">pH {membrane.pHmin}–{membrane.pHmax}</div>
            </div>

            <p className="text-[10px] text-slate-600 italic leading-relaxed">
              Life model: nominal life × (limit/measured)^1.5 for Fe/Mn excess, minus linear SDI and temperature penalties (Arrhenius-style above 25°C). Free Cl above element limit triggers immediate polyamide hydrolysis. Values are estimates for budgeting; actual life depends on cleaning regime, recovery, and incident management. Source: digested from public element data sheets (Hydranautics, DuPont/FilmTec, Toray, LG Chem, Suez).
            </p>
          </div>
        </div>
        );
      })()}

      {/* SAMPLE PICKER POPUP */}
      <AnimatePresence>
        {samplePickerOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSamplePickerOpen(false)}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              className="rb-card w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div>
                  <h3 className="text-sm font-bold text-white">Pick influent sample</h3>
                  <p className="text-[10px] text-slate-500">{samplesList.length} sample{samplesList.length === 1 ? '' : 's'} available {linkedCustomerId ? `for customer #${linkedCustomerId}` : '(all customers)'}</p>
                </div>
                <button onClick={() => setSamplePickerOpen(false)} className="p-1 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="overflow-y-auto p-3 space-y-2">
                {samplesList.length === 0 && <p className="text-xs text-slate-500 text-center py-8">No samples found.</p>}
                {samplesList.map((s: any) => (
                  <button key={s.id} onClick={() => loadFromSample(s)}
                    className="w-full text-left p-3 rb-inner-card hover:bg-sky-500/5 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-white truncate">{s.sampleName || `Sample #${s.id}`}</p>
                        <p className="text-[10px] text-slate-500">
                          {s.reportNumber && `${s.reportNumber} · `}
                          {s.createdAt && new Date(s.createdAt).toLocaleDateString()}
                          {s.customerId && ` · customer #${s.customerId}`}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0 text-[10px]">
                        {s.params?.fe != null && <span className="text-rose-400">Fe {Number(s.params.fe).toFixed(2)}</span>}
                        {s.params?.ph != null && <span className="text-purple-400">pH {Number(s.params.ph).toFixed(1)}</span>}
                        {s.params?.tss != null && <span className="text-amber-400">TSS {Number(s.params.tss).toFixed(1)}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SMART INSERT POPUP — opens from any inline insert button */}
      <AnimatePresence>
        {insertContext && (() => {
          const ctx = insertContext;
          const chain = stages.map(s => s.key);
          return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setInsertContext(null)}>
              <motion.div initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
                className="rb-card w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-slate-800">
                  <div>
                    <h3 className="text-sm font-bold text-white">Insert stage at position {ctx.idx + 1}</h3>
                    <p className="text-[10px] text-slate-500">
                      {ctx.prevKey ? `After: ${EQUIPMENT_LIBRARY[ctx.prevKey].name}` : 'At the very start of the chain'}
                      {ctx.nextKey ? ` · Before: ${EQUIPMENT_LIBRARY[ctx.nextKey].name}` : ' · At the very end'}
                    </p>
                  </div>
                  <button onClick={() => setInsertContext(null)} className="p-1 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="overflow-y-auto p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.values(EQUIPMENT_LIBRARY).map(def => {
                    const Icon = def.icon;
                    const suggestion = evaluateInsertion(def.key, ctx.prevKey, ctx.nextKey, chain);
                    const isSuggested = suggestion.state === 'suggested';
                    const isBlocked   = suggestion.state === 'blocked';
                    return (
                      <button
                        key={def.key}
                        onClick={() => !isBlocked && insertStageAt(def.key, ctx.idx)}
                        disabled={isBlocked}
                        className={`text-left p-3 rb-inner-card border transition-all relative
                          ${isSuggested ? 'border-emerald-400/60 bg-emerald-500/10 hover:bg-emerald-500/20 shadow-lg shadow-emerald-900/30' : ''}
                          ${isBlocked   ? 'border-rose-500/40 bg-rose-500/5 opacity-60 cursor-not-allowed' : ''}
                          ${!isSuggested && !isBlocked ? 'hover:bg-sky-500/5' : ''}
                        `}
                        style={!isSuggested && !isBlocked ? { borderColor: def.color + '40' } : {}}
                        title={suggestion.reason || ''}>
                        {isSuggested && (
                          <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-emerald-500 text-[9px] font-bold uppercase text-slate-950 shadow-lg">
                            ⭐ Recommended
                          </span>
                        )}
                        {isBlocked && (
                          <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-rose-500 text-[9px] font-bold uppercase text-slate-950 shadow-lg">
                            ⛔ Blocked
                          </span>
                        )}
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4" style={{ color: def.color }} />
                          <p className="text-xs font-bold text-white">{def.name}</p>
                        </div>
                        <p className="text-[10px] text-slate-400 leading-relaxed">{def.description}</p>
                        {suggestion.reason && (
                          <p className={`text-[10px] mt-1.5 leading-relaxed font-medium ${isSuggested ? 'text-emerald-300' : isBlocked ? 'text-rose-300' : 'text-slate-500 italic'}`}>
                            {isSuggested && '✓ '}{isBlocked && '✗ '}{suggestion.reason}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="p-3 border-t border-slate-800/50 text-[10px] text-slate-500 flex items-center justify-between">
                  <span><span className="text-emerald-400 font-bold">⭐ Recommended</span> = canonical next stage given upstream chain · <span className="text-rose-400 font-bold">⛔ Blocked</span> = physically/chemically wrong combination</span>
                  <button onClick={() => setInsertContext(null)} className="px-3 py-1 rounded bg-slate-800 text-slate-300 hover:bg-slate-700">Cancel</button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* EQUIPMENT DETAIL POPUP */}
      <AnimatePresence>
        {detailStageId && (() => {
          const stage = stages.find(s => s.id === detailStageId);
          if (!stage) return null;
          const def = EQUIPMENT_LIBRARY[stage.key];
          const stageIdx = stages.findIndex(s => s.id === detailStageId);
          const activeIdxAmongEnabled = stages.slice(0, stageIdx + 1).filter(s => s.enabled).length - 1;
          const result = cascadeResult.stageResults[activeIdxAmongEnabled];
          const Icon = def.icon;
          return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setDetailStageId(null)}>
              <motion.div initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
                className="rb-card w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
                style={{ borderColor: def.color + '60', borderWidth: 2, borderStyle: 'solid' }}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: def.color + '30' }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                         style={{ backgroundColor: def.color + '22', border: `1px solid ${def.color}55` }}>
                      <Icon className="w-5 h-5" style={{ color: def.color }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white truncate">Stage {stageIdx + 1} · {def.name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{def.description}</p>
                    </div>
                  </div>
                  <button onClick={() => setDetailStageId(null)} className="p-1 text-slate-500 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto p-4 space-y-4">
                  {/* Configuration — primary */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">Primary Configuration</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {(def.selectFields || []).map(f => (
                        <div key={f.key} className="space-y-1">
                          <label className="text-[10px] text-slate-500 uppercase">{f.label}</label>
                          <select value={stage.params[f.key]} onChange={e => updateStageParam(stage.id, f.key, e.target.value)}
                            className="w-full bg-rb-elevated border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200">
                            {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                      ))}
                      {def.sliders.map(s => (
                        <ESlider key={s.key} label={s.label} value={stage.params[s.key]} min={s.min} max={s.max} step={s.step} unit={s.unit}
                          color={def.color} onChange={v => updateStageParam(stage.id, s.key, v)} />
                      ))}
                    </div>
                  </div>

                  {/* Advanced */}
                  {(def.advancedSliders?.length || def.advancedSelects?.length) ? (
                    <div>
                      <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">Advanced / Operation</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {(def.advancedSelects || []).map(f => (
                          <div key={f.key} className="space-y-1">
                            <label className="text-[10px] text-slate-500 uppercase">{f.label}</label>
                            <select value={stage.params[f.key]} onChange={e => updateStageParam(stage.id, f.key, e.target.value)}
                              className="w-full bg-rb-elevated border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200">
                              {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                        ))}
                        {(def.advancedSliders || []).map(s => (
                          <div key={s.key}>
                            <ESlider label={s.label} value={stage.params[s.key]} min={s.min} max={s.max} step={s.step} unit={s.unit}
                              color={def.color} onChange={v => updateStageParam(stage.id, s.key, v)} />
                            {s.info && <p className="text-[10px] text-slate-600 mt-0.5">{s.info}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Live in/out & gauges */}
                  {result && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div className="rb-inner-card p-3 space-y-2">
                        <p className="text-[10px] uppercase text-slate-500 font-bold">Inlet → Outlet (live)</p>
                        {(() => {
                          // Show whichever parameters are actually present on this stream so
                          // wastewater units (COD/BOD/NH₃…) and water-treatment units (Fe/Mn/SDI)
                          // each surface their own live influent→effluent values.
                          const rows: { l: string; i: number; o: number; d: number; u: string; target: number }[] = [];
                          const add = (l: string, i: number, o: number, d: number, u: string, target: number) => rows.push({ l, i, o, d, u, target });
                          if ((result.in.fe ?? 0) > 0 || (result.in.mn ?? 0) > 0 || (result.in.sdi ?? 0) > 0) {
                            add('Fe', result.in.fe, result.out.fe, 3, 'mg/L', 0.01);
                            add('Mn', result.in.mn, result.out.mn, 3, 'mg/L', 0.05);
                            add('SDI', result.in.sdi, result.out.sdi, 1, '', 3);
                          }
                          add('TSS', result.in.tss, result.out.tss, 1, 'mg/L', 5);
                          add('Turbidity', result.in.turbidity, result.out.turbidity, 2, 'NTU', 1);
                          const ww: [string, keyof Water, number, string, number][] = [
                            ['COD', 'cod', 1, 'mg/L', 125], ['BOD₅', 'bod5', 1, 'mg/L', 25],
                            ['NH₃-N', 'nh3', 1, 'mg/L', 10], ['Total N', 'totalNitrogen', 1, 'mg/L', 15],
                            ['Total P', 'totalPhosphorus', 1, 'mg/L', 2], ['Oil & Grease', 'oilGrease', 1, 'mg/L', 10],
                          ];
                          ww.forEach(([l, k, d, u, target]) => {
                            const iv = (result.in[k] as number) ?? 0;
                            const ov = (result.out[k] as number) ?? 0;
                            if (iv > 0 || ov > 0) add(l, iv, ov, d, u, target);
                          });
                          return rows;
                        })().map(m => (
                          <div key={m.l} className="flex items-center justify-between text-xs">
                            <span className="text-slate-400">{m.l}</span>
                            <span className="text-slate-300 tabular-nums">
                              <span className="text-slate-600">{m.i.toFixed(m.d)}</span>
                              <ArrowRight className="inline w-3 h-3 mx-1 text-slate-700" />
                              <span className={m.o <= m.target ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>
                                {m.o.toFixed(m.d)} {m.u}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="rb-inner-card p-3 space-y-2">
                        <p className="text-[10px] uppercase text-slate-500 font-bold">Stage cost (est)</p>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-400">Capex</span>
                          <span className="text-white font-bold tabular-nums">£{(result.capexGBP/1000).toFixed(1)}k</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-400">Opex / year</span>
                          <span className="text-white font-bold tabular-nums">£{(result.opexGBPperYear/1000).toFixed(2)}k</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-400">10-yr total</span>
                          <span className="text-white font-bold tabular-nums">£{((result.capexGBP + result.opexGBPperYear*10)/1000).toFixed(0)}k</span>
                        </div>
                        {result.notes && (
                          <div className="pt-2 border-t border-slate-800 text-[10px] text-slate-500 italic">
                            {result.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Science */}
                  {def.scienceNotes && def.scienceNotes.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Beaker className="w-3.5 h-3.5 text-sky-400" /> Engineering Notes
                      </h4>
                      <div className="space-y-1.5">
                        {def.scienceNotes.map((n, i) => (
                          <div key={i} className="text-[11px] text-slate-400 leading-relaxed flex gap-2">
                            <span className="text-sky-500 shrink-0">•</span>
                            <span>{n}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-slate-800/50 flex items-center justify-between">
                  <p className="text-[10px] text-slate-500">Changes save automatically and re-simulate the train.</p>
                  <button onClick={() => setDetailStageId(null)}
                    className="px-4 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20">
                    Done
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* EQUIPMENT PICKER POPUP */}
      <AnimatePresence>
        {equipmentPickerOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setEquipmentPickerOpen(false)}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              className="rb-card w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <h3 className="text-sm font-bold text-white">Add equipment stage</h3>
                <button onClick={() => setEquipmentPickerOpen(false)} className="p-1 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="overflow-y-auto p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.values(EQUIPMENT_LIBRARY).map(def => {
                  const Icon = def.icon;
                  return (
                    <button key={def.key} onClick={() => addStage(def.key)}
                      className="text-left p-3 rb-inner-card border hover:bg-sky-500/5 transition-colors"
                      style={{ borderColor: def.color + '40' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4" style={{ color: def.color }} />
                        <p className="text-xs font-bold text-white">{def.name}</p>
                      </div>
                      <p className="text-[10px] text-slate-500 leading-relaxed">{def.description}</p>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Stage validation — return list of checks (pass/warn/fail) ──────────
   Each rule names a physical envelope: velocity, EBCT, bed depth, etc.
   Drawn as small chips beneath the inline KPI strip for live defensibility. */
interface ValidationCheck { label: string; status: 'pass' | 'warn' | 'fail'; detail: string; }

function validateStage(stage: StageConfig, water: Water): ValidationCheck[] {
  const out: ValidationCheck[] = [];
  const p = stage.params;
  switch (stage.key) {
    case 'aeration_column': {
      const area = Math.PI * Math.pow(p.columnDiameter / 2, 2);
      const v = water.flow / area;
      out.push({ label: 'Sup. velocity', status: v > 80 ? 'fail' : v > 60 ? 'warn' : 'pass', detail: `${v.toFixed(0)} m/h (envelope ≤ 80)` });
      const ebct = (area * p.columnHeight / water.flow) * 3600;
      out.push({ label: 'EBCT', status: ebct < 15 ? 'fail' : ebct < 30 ? 'warn' : 'pass', detail: `${ebct.toFixed(0)} s (≥30 ideal)` });
      out.push({ label: 'Air:Water', status: p.airToWaterRatio < 4 ? 'warn' : 'pass', detail: `${p.airToWaterRatio} (≥5 typical)` });
      break;
    }
    case 'cascade_aerator': {
      out.push({ label: 'Steps', status: p.stepCount < 3 ? 'warn' : 'pass', detail: `${p.stepCount} steps (≥3 for DO uplift)` });
      out.push({ label: 'Weir loading', status: 'pass', detail: `${p.weirLength.toFixed(1)} m weir` });
      break;
    }
    case 'greensand': {
      const dutyVessels = p.arrangement === 'duplex_duty_standby' ? 1 : 2;
      const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2) * dutyVessels;
      const v = water.flow / area;
      out.push({ label: 'v_service', status: v > 12 ? 'fail' : v < 5 ? 'warn' : 'pass', detail: `${v.toFixed(1)} m/h (5–12)` });
      out.push({ label: 'Bed depth', status: p.bedDepth < 0.7 ? 'warn' : 'pass', detail: `${(p.bedDepth*1000).toFixed(0)} mm (≥750 mm)` });
      out.push({ label: 'pH window', status: (water.ph < 6.2 || water.ph > 8.8) ? 'fail' : 'pass', detail: `pH ${water.ph.toFixed(1)} (6.2–8.8)` });
      if (p.media === 'greensand_plus') out.push({ label: 'KMnO₄', status: p.kmno4Dose < 1 ? 'warn' : 'pass', detail: `${p.kmno4Dose} mg/L` });
      if (p.media === 'birm') out.push({ label: 'DO available', status: water.doIn < water.fe * 0.15 ? 'fail' : 'pass', detail: `${water.doIn.toFixed(1)} mg/L (≥${(water.fe*0.15).toFixed(2)})` });
      break;
    }
    case 'uf': {
      const totalArea = p.moduleArea * p.numModules;
      const flux = (water.flow * 1000) / totalArea;
      out.push({ label: 'Flux', status: flux > 90 ? 'fail' : flux > 70 ? 'warn' : flux < 25 ? 'warn' : 'pass', detail: `${flux.toFixed(0)} LMH (30–80 design)` });
      out.push({ label: 'Recovery', status: p.recovery > 95 ? 'warn' : 'pass', detail: `${p.recovery.toFixed(0)} %` });
      out.push({ label: 'Fe carryover', status: water.fe > 0.1 ? 'fail' : water.fe > 0.05 ? 'warn' : 'pass', detail: `${water.fe.toFixed(3)} mg/L (≤0.1)` });
      break;
    }
    case 'multimedia':
    case 'dual_media_filter':
    case 'sand_filter_single': {
      const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2) * p.numVessels;
      const v = water.flow / area;
      out.push({ label: 'v_approach', status: v > 15 ? 'fail' : v > 12 ? 'warn' : v < 5 ? 'warn' : 'pass', detail: `${v.toFixed(1)} m/h (7–15)` });
      const totalBed = (p.bedDepth ?? ((p.anthraciteDepth ?? 0) + (p.sandDepth ?? 0) + (p.garnetDepth ?? 0)));
      out.push({ label: 'Bed depth', status: totalBed < 0.6 ? 'warn' : 'pass', detail: `${(totalBed*1000).toFixed(0)} mm (≥700 mm)` });
      break;
    }
    case 'gac_filter': {
      const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2) * p.numVessels;
      const bedVol = area * p.bedDepth;
      const ebctMin = (bedVol / water.flow) * 60;
      out.push({ label: 'EBCT', status: ebctMin < p.ebctMin ? 'fail' : 'pass', detail: `${ebctMin.toFixed(1)} min (≥${p.ebctMin})` });
      const v = water.flow / area;
      out.push({ label: 'v_approach', status: v > 15 ? 'warn' : 'pass', detail: `${v.toFixed(1)} m/h (≤15)` });
      break;
    }
    case 'cartridge_polish': {
      const cartCount = p.housings * p.cartridgesPerHousing;
      const flowPerCart = water.flow / cartCount;
      out.push({ label: 'Flow/cartridge', status: flowPerCart > 4 ? 'warn' : 'pass', detail: `${flowPerCart.toFixed(2)} m³/h (≤4)` });
      break;
    }
    case 'pacl_dosing': {
      out.push({ label: 'Dose', status: p.dose < 0.5 ? 'warn' : p.dose > 5 ? 'warn' : 'pass', detail: `${p.dose.toFixed(1)} mg/L Al (0.5–2.5 typical)` });
      out.push({ label: 'Mix time', status: p.contactTime < 30 ? 'warn' : 'pass', detail: `${p.contactTime} s (≥30)` });
      break;
    }
    case 'uv_disinfection': {
      out.push({ label: 'Clean upstream', status: water.sdi > 3 ? 'fail' : water.turbidity > 0.3 ? 'warn' : 'pass', detail: `SDI ${water.sdi.toFixed(1)} · turb ${water.turbidity.toFixed(2)} NTU` });
      out.push({ label: 'UVT envelope', status: p.uvtPct < 70 ? 'fail' : p.uvtPct < 85 ? 'warn' : 'pass', detail: `${p.uvtPct}% (≥85% RO grade)` });
      break;
    }
  }
  return out;
}

function ValidationChips({ checks }: { checks: ValidationCheck[] }) {
  if (checks.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-800/50">
      {checks.map((c, i) => {
        const colour =
          c.status === 'pass' ? { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300' } :
          c.status === 'warn' ? { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-300' } :
                                 { bg: 'bg-rose-500/15',    border: 'border-rose-500/40',    text: 'text-rose-300' };
        const glyph = c.status === 'pass' ? '✓' : c.status === 'warn' ? '!' : '✗';
        return (
          <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full border ${colour.bg} ${colour.border} ${colour.text}`} title={c.detail}>
            <span className="font-bold mr-0.5">{glyph}</span>{c.label}: <span className="font-mono">{c.detail}</span>
          </span>
        );
      })}
    </div>
  );
}

/* ─── Inline insert button between stages ─── */
function InsertBetween({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex items-center gap-2 my-1 group cursor-pointer" onClick={onClick}>
      <div className="flex-1 border-t border-dashed border-slate-800 group-hover:border-sky-500/40 transition-colors" />
      <button
        type="button"
        className="px-3 py-1 rounded-full bg-rb-elevated border border-slate-800 text-[10px] text-slate-500 group-hover:text-sky-300 group-hover:border-sky-500/50 group-hover:bg-sky-500/10 transition-all font-bold uppercase tracking-wider flex items-center gap-1"
      >
        <Plus className="w-3 h-3" /> Insert stage here
      </button>
      <div className="flex-1 border-t border-dashed border-slate-800 group-hover:border-sky-500/40 transition-colors" />
    </div>
  );
}

/* ─── Stage Output Bar (compact KPI strip per stage) ─── */
function StageOutputBar({ result, color }: { result: StageResult; color: string }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 pt-2 border-t border-slate-800/50">
      {[
        { label: 'Fe',   in: result.in.fe, out: result.out.fe, d: 3 },
        { label: 'Mn',   in: result.in.mn, out: result.out.mn, d: 3 },
        { label: 'SDI',  in: result.in.sdi, out: result.out.sdi, d: 1 },
        { label: 'Turb', in: result.in.turbidity, out: result.out.turbidity, d: 2 },
        { label: 'TSS',  in: result.in.tss, out: result.out.tss, d: 1 },
      ].map((m, i) => (
        <div key={i} className="text-[10px]">
          <p className="text-slate-500 uppercase">{m.label}</p>
          <p className="text-slate-300 tabular-nums">
            <span className="text-slate-600">{fmt(m.in, m.d)}</span>
            <ArrowRight className="inline w-2.5 h-2.5 mx-0.5 text-slate-700" />
            <span className="font-bold" style={{ color }}>{fmt(m.out, m.d)}</span>
          </p>
        </div>
      ))}
      <div className="text-[10px]">
        <p className="text-slate-500 uppercase">Capex / Opex</p>
        <p className="text-slate-300 tabular-nums">£{(result.capexGBP/1000).toFixed(0)}k / £{(result.opexGBPperYear/1000).toFixed(1)}k·yr</p>
      </div>
      {result.notes && (
        <div className="col-span-2 sm:col-span-3 lg:col-span-6 text-[10px] text-slate-500 italic pt-1">
          {result.notes}
        </div>
      )}
    </div>
  );
}

/* ─── Process Diagram (SVG, fontSize >= 12 throughout) ─── */
function ProcessDiagram({ stages, cascade, influent }: { stages: StageConfig[]; cascade: StageResult[]; influent: Water }) {
  const activeStages = stages.filter(s => s.enabled);
  const w = 1700;
  const h = 520;
  const startX = 40, endX = w - 40;
  const influentW = 220, roW = 220;
  // Wider pipe lanes between blocks (Sankey-style curves).
  const gap = 70;
  const stageWidth = activeStages.length > 0
    ? Math.max(200, (endX - startX - influentW - roW - 2 * gap - (activeStages.length - 1) * gap) / Math.max(1, activeStages.length))
    : 200;
  const stageY = 130;
  const stageH = 260;

  const finalWater = cascade.length > 0 ? cascade[cascade.length - 1].out : influent;

  const meets = finalWater.fe <= RO_TARGETS.fe && finalWater.sdi <= RO_TARGETS.sdi;
  const roColour = meets ? '#10b981' : '#f43f5e';

  // Precompute pipe geometry so we can render bezier connectors that flow
  // between successive blocks. Each pipe ribbon uses a gradient from the
  // upstream block's accent colour to the downstream block's accent.
  type Block = { x: number; cx: number; cy: number; w: number; color: string };
  const blocks: Block[] = [];
  blocks.push({ x: startX, cx: startX + influentW, cy: stageY + stageH / 2, w: influentW, color: '#38bdf8' });
  activeStages.forEach((stage, i) => {
    const x = startX + influentW + gap + i * (stageWidth + gap);
    blocks.push({ x, cx: x + stageWidth, cy: stageY + stageH / 2, w: stageWidth, color: EQUIPMENT_LIBRARY[stage.key].color });
  });
  if (activeStages.length > 0) {
    const x = startX + influentW + gap + activeStages.length * (stageWidth + gap);
    blocks.push({ x, cx: x + roW, cy: stageY + stageH / 2, w: roW, color: roColour });
  }

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      <defs>
        <linearGradient id="diagWater" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#0f2847" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="diagRoOk" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#0f2847" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="diagRoFail" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#0f2847" stopOpacity="0.9" />
        </linearGradient>
        {/* Per-pipe gradients: from block N's color → block N+1's color. */}
        {blocks.slice(0, -1).map((b, i) => {
          const next = blocks[i + 1];
          return (
            <linearGradient key={`pg${i}`} id={`pipeGrad${i}`} x1={b.cx} y1={b.cy} x2={next.x} y2={next.cy} gradientUnits="userSpaceOnUse">
              <stop offset="0%"  stopColor={b.color}    stopOpacity="0.95" />
              <stop offset="100%" stopColor={next.color} stopOpacity="0.95" />
            </linearGradient>
          );
        })}
        {/* Soft glow filter for the pipes */}
        <filter id="pipeGlow" x="-10%" y="-50%" width="120%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {/* Flowing dashes animation keyframe values are inline via animate elements */}
      </defs>

      {/* Title */}
      <text x={w/2} y="36" fill="#cbd5e1" fontSize="24" textAnchor="middle" fontWeight="bold" letterSpacing="2">
        LIVE PROCESS SIMULATION
      </text>
      <text x={w/2} y="64" fill="#64748b" fontSize="14" textAnchor="middle" letterSpacing="1">
        {activeStages.length} ACTIVE STAGE{activeStages.length === 1 ? '' : 'S'} · INFLUENT → STAGES → RO FEED
      </text>

      {/* Sankey-style pipe ribbons between blocks — drawn FIRST so blocks sit on top. */}
      {blocks.slice(0, -1).map((b, i) => {
        const next = blocks[i + 1];
        const x1 = b.cx, y1 = b.cy;
        const x2 = next.x, y2 = next.cy;
        const cx1 = x1 + (x2 - x1) * 0.55;
        const cx2 = x2 - (x2 - x1) * 0.55;
        const path = `M ${x1} ${y1} C ${cx1} ${y1}, ${cx2} ${y2}, ${x2} ${y2}`;
        return (
          <g key={`pipe${i}`}>
            {/* Outer glow ribbon */}
            <path d={path} fill="none" stroke={`url(#pipeGrad${i})`} strokeWidth="36" strokeOpacity="0.18" strokeLinecap="round" filter="url(#pipeGlow)" />
            {/* Main ribbon */}
            <path d={path} fill="none" stroke={`url(#pipeGrad${i})`} strokeWidth="22" strokeOpacity="0.85" strokeLinecap="round" />
            {/* Inner highlight pipe */}
            <path d={path} fill="none" stroke="#0f2847" strokeWidth="6" strokeOpacity="0.7" strokeLinecap="round" />
            {/* Animated flowing dashes — strokeDashoffset animates to mimic flow */}
            <path d={path} fill="none" stroke="#f8fafc" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.9"
                  strokeDasharray="8 22">
              <animate attributeName="stroke-dashoffset" from="0" to="-60" dur={`${2.4 + i * 0.2}s`} repeatCount="indefinite" />
            </path>
            {/* Pulsing dot at midpoint */}
            <circle r="5" fill={b.color}>
              <animateMotion dur={`${3 + i * 0.3}s`} repeatCount="indefinite" path={path} />
              <animate attributeName="r" values="3;6;3" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
              <animate attributeName="fill-opacity" values="0.5;1;0.5" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}

      {/* Influent block */}
      <rect x={startX} y={stageY} width={influentW} height={stageH} rx="14" fill="url(#diagWater)" stroke="#38bdf8" strokeWidth="3" />
      <rect x={startX} y={stageY} width={influentW} height="42" rx="14" fill="#38bdf8" fillOpacity="0.15" />
      <text x={startX + influentW/2} y={stageY + 28} fill="#38bdf8" fontSize="20" textAnchor="middle" fontWeight="bold" letterSpacing="1">INFLUENT</text>
      <line x1={startX + 20} y1={stageY + 48} x2={startX + influentW - 20} y2={stageY + 48} stroke="#1e293b" strokeWidth="1" />
      <text x={startX + influentW/2} y={stageY + 82} fill="#e2e8f0" fontSize="17" textAnchor="middle" fontWeight="bold">
        {influent.flow.toFixed(1)} m³/h
      </text>
      <text x={startX + 26} y={stageY + 116} fill="#94a3b8" fontSize="13">Fe</text>
      <text x={startX + influentW - 26} y={stageY + 116} fill="#f43f5e" fontSize="15" textAnchor="end" fontWeight="bold">{influent.fe.toFixed(3)} mg/L</text>
      <text x={startX + 26} y={stageY + 140} fill="#94a3b8" fontSize="13">Mn</text>
      <text x={startX + influentW - 26} y={stageY + 140} fill="#a855f7" fontSize="15" textAnchor="end" fontWeight="bold">{influent.mn.toFixed(3)} mg/L</text>
      <text x={startX + 26} y={stageY + 164} fill="#94a3b8" fontSize="13">SDI</text>
      <text x={startX + influentW - 26} y={stageY + 164} fill="#fbbf24" fontSize="15" textAnchor="end" fontWeight="bold">{influent.sdi.toFixed(1)}</text>
      <text x={startX + 26} y={stageY + 188} fill="#94a3b8" fontSize="13">TSS</text>
      <text x={startX + influentW - 26} y={stageY + 188} fill="#fbbf24" fontSize="15" textAnchor="end" fontWeight="bold">{influent.tss.toFixed(1)} mg/L</text>
      <text x={startX + 26} y={stageY + 212} fill="#94a3b8" fontSize="13">Turbidity</text>
      <text x={startX + influentW - 26} y={stageY + 212} fill="#fbbf24" fontSize="15" textAnchor="end" fontWeight="bold">{influent.turbidity.toFixed(2)} NTU</text>
      <text x={startX + 26} y={stageY + 236} fill="#94a3b8" fontSize="13">pH</text>
      <text x={startX + influentW - 26} y={stageY + 236} fill="#cbd5e1" fontSize="15" textAnchor="end" fontWeight="bold">{influent.ph.toFixed(1)}</text>

      {/* Stages */}
      {activeStages.map((stage, i) => {
        const def = EQUIPMENT_LIBRARY[stage.key];
        const x = startX + influentW + gap + i * (stageWidth + gap);
        const result = cascade[i];
        const sw = stageWidth;
        const midX = x + sw / 2;
        return (
          <g key={stage.id}>
            {/* Stage block */}
            <rect x={x} y={stageY} width={sw} height={stageH} rx="14"
              fill="url(#diagWater)" stroke={def.color} strokeWidth="3" />
            {/* Header strip */}
            <rect x={x} y={stageY} width={sw} height="42" rx="14" fill={def.color} fillOpacity="0.18" />
            {/* Stage number badge */}
            <circle cx={x + 26} cy={stageY + 24} r="16" fill={def.color} opacity="0.4" />
            <text x={x + 26} y={stageY + 30} fill="#0f172a" fontSize="15" textAnchor="middle" fontWeight="bold">{i + 1}</text>
            <text x={midX} y={stageY + 30} fill={def.color} fontSize="17" textAnchor="middle" fontWeight="bold" letterSpacing="0.5">
              {def.name.split('(')[0].trim()}
            </text>
            <line x1={x + 18} y1={stageY + 48} x2={x + sw - 18} y2={stageY + 48} stroke="#1e293b" strokeWidth="1" />
            {result && (
              <>
                {/* Outlet KPIs */}
                <text x={x + 22} y={stageY + 80} fill="#94a3b8" fontSize="13">Fe out</text>
                <text x={x + sw - 22} y={stageY + 80} fill={result.out.fe <= 0.01 ? '#10b981' : '#e2e8f0'} fontSize="15" textAnchor="end" fontWeight="bold">{result.out.fe.toFixed(3)}</text>
                <text x={x + 22} y={stageY + 104} fill="#94a3b8" fontSize="13">Mn out</text>
                <text x={x + sw - 22} y={stageY + 104} fill="#e2e8f0" fontSize="15" textAnchor="end" fontWeight="bold">{result.out.mn.toFixed(3)}</text>
                <text x={x + 22} y={stageY + 128} fill="#94a3b8" fontSize="13">SDI</text>
                <text x={x + sw - 22} y={stageY + 128} fill={result.out.sdi <= 3 ? '#10b981' : '#e2e8f0'} fontSize="15" textAnchor="end" fontWeight="bold">{result.out.sdi.toFixed(1)}</text>
                <text x={x + 22} y={stageY + 152} fill="#94a3b8" fontSize="13">TSS</text>
                <text x={x + sw - 22} y={stageY + 152} fill="#e2e8f0" fontSize="15" textAnchor="end" fontWeight="bold">{result.out.tss.toFixed(2)}</text>
                <text x={x + 22} y={stageY + 176} fill="#94a3b8" fontSize="13">Turb</text>
                <text x={x + sw - 22} y={stageY + 176} fill="#e2e8f0" fontSize="15" textAnchor="end" fontWeight="bold">{result.out.turbidity.toFixed(2)} NTU</text>
                <line x1={x + 18} y1={stageY + 196} x2={x + sw - 18} y2={stageY + 196} stroke="#1e293b" strokeWidth="1" />
                <text x={x + 22} y={stageY + 220} fill="#64748b" fontSize="13">Capex</text>
                <text x={x + sw - 22} y={stageY + 220} fill="#fbbf24" fontSize="14" textAnchor="end" fontWeight="bold">£{(result.capexGBP/1000).toFixed(0)}k</text>
                <text x={x + 22} y={stageY + 244} fill="#64748b" fontSize="13">Opex/yr</text>
                <text x={x + sw - 22} y={stageY + 244} fill="#fbbf24" fontSize="14" textAnchor="end" fontWeight="bold">£{(result.opexGBPperYear/1000).toFixed(1)}k</text>
              </>
            )}
          </g>
        );
      })}

      {/* Final RO block */}
      {activeStages.length > 0 && (() => {
        const ax = startX + influentW + gap + activeStages.length * (stageWidth + gap);
        return (
          <g>
            <rect x={ax} y={stageY} width={roW} height={stageH} rx="14" fill={`url(#${meets ? 'diagRoOk' : 'diagRoFail'})`} stroke={roColour} strokeWidth="3" />
            <rect x={ax} y={stageY} width={roW} height="42" rx="14" fill={roColour} fillOpacity="0.18" />
            <text x={ax + roW/2} y={stageY + 28} fill={roColour} fontSize="20" textAnchor="middle" fontWeight="bold" letterSpacing="1">RO FEED</text>
            <line x1={ax + 20} y1={stageY + 48} x2={ax + roW - 20} y2={stageY + 48} stroke="#1e293b" strokeWidth="1" />
            <text x={ax + 26} y={stageY + 84} fill="#94a3b8" fontSize="13">Fe</text>
            <text x={ax + roW - 26} y={stageY + 84} fill="#e2e8f0" fontSize="15" textAnchor="end" fontWeight="bold">{finalWater.fe.toFixed(3)} mg/L</text>
            <text x={ax + 26} y={stageY + 108} fill="#94a3b8" fontSize="13">Mn</text>
            <text x={ax + roW - 26} y={stageY + 108} fill="#e2e8f0" fontSize="15" textAnchor="end" fontWeight="bold">{finalWater.mn.toFixed(3)} mg/L</text>
            <text x={ax + 26} y={stageY + 132} fill="#94a3b8" fontSize="13">SDI</text>
            <text x={ax + roW - 26} y={stageY + 132} fill="#e2e8f0" fontSize="15" textAnchor="end" fontWeight="bold">{finalWater.sdi.toFixed(1)}</text>
            <text x={ax + 26} y={stageY + 156} fill="#94a3b8" fontSize="13">TSS</text>
            <text x={ax + roW - 26} y={stageY + 156} fill="#e2e8f0" fontSize="15" textAnchor="end" fontWeight="bold">{finalWater.tss.toFixed(2)} mg/L</text>
            <line x1={ax + 18} y1={stageY + 176} x2={ax + roW - 18} y2={stageY + 176} stroke="#1e293b" strokeWidth="1" />
            <text x={ax + roW/2} y={stageY + 198} fill="#64748b" fontSize="13" textAnchor="middle">target Fe ≤ {RO_TARGETS.fe} mg/L</text>
            <text x={ax + roW/2} y={stageY + 218} fill="#64748b" fontSize="13" textAnchor="middle">target SDI ≤ {RO_TARGETS.sdi}</text>
            <text x={ax + roW/2} y={stageY + 246} fill={roColour} fontSize="20" textAnchor="middle" fontWeight="bold">
              {meets ? '✓ PASS' : '✗ FAIL'}
            </text>
          </g>
        );
      })()}

      {/* Footer legend */}
      <text x={startX} y={h - 16} fill="#475569" fontSize="12">
        Each stage outlet is the next stage's inlet · Hard targets: Fe ≤ {RO_TARGETS.fe} mg/L · SDI ≤ {RO_TARGETS.sdi} · TSS ≤ {RO_TARGETS.tss} mg/L
      </text>
      <text x={w - startX} y={h - 16} fill="#64748b" fontSize="12" textAnchor="end" fontWeight="bold" letterSpacing="1">
        REYNOLDS &amp; BAUHM SMART TOOL 2.0
      </text>
    </svg>
  );
}
